/**
 * Elite Batch 16 Part 5 — Workflow Templates 1528–1547
 * Calendly booking CRM sync, case-study consumption nurture, case-study drip
 * after pricing, certification expiry reminder, certification completion celebration,
 * champion left account alert, channel partner onboarding, churn risk detector,
 * closed-lost reason capture plus recycle, closed-won onboarding handoff,
 * cold outreach sequencer, community webinar series nurture, competitor comparison
 * page nurture, compliance doc-request nurture, contact form to CRM, contact-us
 * inquiry triage, content recommendation by funnel stage, content recommendation
 * by role, contract renewal for B2B, and contract sent signature chase workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 16 PART 5 — TEMPLATES 1528–1547
// =============================================================================

export const ELITE_BATCH16_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1528. CALENDLY BOOKING TO CRM SYNC (7 nodes)
  // ===================================================================

  "Calendly Booking to CRM Sync": {
    summary:
      "Receives Calendly booking events via webhook, extracts booking data with AI, looks up the contact in HubSpot, creates or updates the contact record, logs a meeting activity in HubSpot, notifies the assigned rep via Slack, and sends a pre-meeting brief email to the rep.",
    nodes: [
      trigger("Webhook: Calendly booking created", "webhook_app", {
        source: "calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when a new meeting is booked on Calendly — including discovery calls, demos, support sessions, and onboarding kickoffs",
        setup:
          "Configure Calendly webhook subscription for invitee.created events. Set OpenAI API key for AI extraction. Provide HubSpot private app token for CRM operations. Set Slack webhook URL for rep notifications. Configure email for pre-meeting briefs.",
        inputFields: [],
      }),

      // 2. AI extract booking data
      aiNode(
        "2",
        "AI Extract Booking Data",
        280,
        "You are a calendar booking parser. Extract all meeting details from a raw Calendly webhook payload regardless of structure. Find the invitee name, email, phone, event type, scheduled date/time, timezone, meeting duration, any custom questions/answers, and the assigned host. Respond JSON: {invitee_name: string, invitee_email: string, invitee_phone: string, event_type: string, meeting_date: string, meeting_time: string, timezone: string, duration_minutes: string, custom_answers: string, host_name: string, host_email: string, meeting_url: string, cancel_url: string}",
        "Raw Calendly webhook payload: {webhook_payload}. Extract all booking details regardless of field naming conventions.",
        [
          { field: "invitee_name", as: "invitee_name", type: "string" },
          { field: "invitee_email", as: "invitee_email", type: "string" },
          { field: "invitee_phone", as: "invitee_phone", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "meeting_time", as: "meeting_time", type: "string" },
          { field: "timezone", as: "timezone", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "string" },
          { field: "custom_answers", as: "custom_answers", type: "string" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "meeting_url", as: "meeting_url", type: "string" },
          { field: "cancel_url", as: "cancel_url", type: "string" },
        ],
      ),

      // 3. Contact lookup HubSpot
      hubspotGetNode(
        "3",
        "Lookup Contact in HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "hs_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "hs_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "hs_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "hs_lifecycle_stage",
            type: "string",
          },
          { name: "total", customName: "contact_match_count", type: "number" },
        ],
      ),

      // 4. Contact creator/updater HubSpot
      hubspotPostNode(
        "4",
        "Create or Update Contact in HubSpot",
        760,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{invitee_email}","firstname":"{invitee_name}","phone":"{invitee_phone}","hs_lead_status":"OPEN","notes_last_contacted":"{meeting_date}","calendly_event_type":"{event_type}","calendly_custom_answers":"{custom_answers}"}',
          },
        ],
        [
          { name: "id", customName: "crm_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_date",
            type: "string",
          },
        ],
      ),

      // 5. Meeting activity logger HubSpot
      hubspotPostNode(
        "5",
        "Log Meeting Activity in HubSpot",
        1000,
        "crm/v3/objects/meetings",
        [
          {
            key: "properties",
            value:
              '{"hs_meeting_title":"{event_type} with {invitee_name}","hs_meeting_start_time":"{meeting_date}T{meeting_time}","hs_meeting_end_time":"{meeting_date}","hs_meeting_outcome":"SCHEDULED","hs_meeting_body":"Meeting Type: {event_type}\\nDuration: {duration_minutes} min\\nTimezone: {timezone}\\nCustom Answers: {custom_answers}\\nMeeting Link: {meeting_url}","hs_meeting_external_url":"{meeting_url}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{crm_contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":200}]}]',
          },
        ],
        [
          { name: "id", customName: "hs_meeting_id", type: "string" },
          {
            name: "properties.hs_meeting_title",
            customName: "meeting_title",
            type: "string",
          },
        ],
      ),

      // 6. Rep notification Slack
      slackNode(
        "6",
        "Notify Assigned Rep via Slack",
        1240,
        "New {event_type} booked on Calendly\\n*{invitee_name}* ({invitee_email})\\nDate: {meeting_date} at {meeting_time} ({timezone})\\nDuration: {duration_minutes} min\\nCompany: {hs_company}\\nLifecycle Stage: {hs_lifecycle_stage}\\nCustom Answers: {custom_answers}\\nMeeting Link: {meeting_url}",
      ),

      // 7. Pre-meeting brief email
      emailNode(
        "7",
        "Send Pre-Meeting Brief to Rep",
        1480,
        "{host_email}",
        "Pre-Meeting Brief: {event_type} with {invitee_name} on {meeting_date}",
        "<h2>Meeting Brief</h2><p><strong>Invitee:</strong> {invitee_name} ({invitee_email})</p><p><strong>Company:</strong> {hs_company}</p><p><strong>Lifecycle Stage:</strong> {hs_lifecycle_stage}</p><p><strong>Event Type:</strong> {event_type}</p><p><strong>Date/Time:</strong> {meeting_date} at {meeting_time} ({timezone})</p><p><strong>Duration:</strong> {duration_minutes} minutes</p><p><strong>Custom Answers:</strong> {custom_answers}</p><p><strong>Meeting Link:</strong> <a href='{meeting_url}'>{meeting_url}</a></p><p><strong>CRM Contact ID:</strong> {crm_contact_id}</p><p>Review the contact in HubSpot before the meeting for full history.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1529. CASE-STUDY CONSUMPTION NURTURE (7 nodes)
  // ===================================================================

  "Case-Study Consumption Nurture": {
    summary:
      "Triggers when a lead views a case study via webhook, extracts lead context with AI, loads full lead profile from HubSpot, uses AI to select related content, sends a follow-up email with social proof, waits 5 days, then sends a demo offer email.",
    nodes: [
      trigger("Webhook: Case study viewed", "webhook_app", {
        source: "website_analytics",
        event: "casestudy.viewed",
        howItStarts:
          "Fires when a known lead views a case study page on the website, captured via tracking pixel, Segment event, or CMS webhook",
        setup:
          "Configure website tracking to fire webhook on case study page views with visitor identity. Set OpenAI API key for AI-powered content selection. Provide HubSpot private app token for lead data. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract lead context
      aiNode(
        "2",
        "AI Extract Lead and Case Study Context",
        280,
        "You are a marketing analytics parser. Extract the lead identifier, case study title, industry featured in the case study, the page URL, time spent on page, and any UTM parameters from the raw webhook payload. Respond JSON: {lead_email: string, lead_name: string, case_study_title: string, case_study_industry: string, case_study_url: string, time_on_page_seconds: string, utm_source: string, utm_campaign: string, referrer_url: string}",
        "Raw webhook payload: {webhook_payload}. Extract all lead and case study viewing data regardless of field naming.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "case_study_title", as: "case_study_title", type: "string" },
          {
            field: "case_study_industry",
            as: "case_study_industry",
            type: "string",
          },
          { field: "case_study_url", as: "case_study_url", type: "string" },
          {
            field: "time_on_page_seconds",
            as: "time_on_page_seconds",
            type: "string",
          },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "referrer_url", as: "referrer_url", type: "string" },
        ],
      ),

      // 3. Lead context loader HubSpot
      hubspotGetNode(
        "3",
        "Load Lead Profile from HubSpot",
        520,
        "crm/v3/objects/contacts/{lead_email}?idProperty=email&properties=firstname,lastname,company,industry,lifecyclestage,num_associated_deals,hs_lead_status",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. AI related content selector
      aiNode(
        "4",
        "AI Select Related Follow-Up Content",
        760,
        "You are a content marketing strategist. Given the case study the lead just read and their company profile, recommend the most compelling follow-up content. Select a related case study from a similar industry, a relevant blog post, and a specific product feature to highlight. Make sure the content builds on what they already read and moves them closer to a buying decision. Respond JSON: {related_case_study_title: string, related_case_study_url: string, blog_post_title: string, blog_post_url: string, feature_highlight: string, value_proposition: string, personalized_cta: string, follow_up_angle: string}",
        "Lead: {first_name} {last_name} at {company_name} (Industry: {lead_industry}, Stage: {lifecycle_stage}). Just read case study: {case_study_title} (Industry: {case_study_industry}). Time on page: {time_on_page_seconds}s. Select the strongest follow-up content to deepen engagement.",
        [
          {
            field: "related_case_study_title",
            as: "related_case_study_title",
            type: "string",
          },
          {
            field: "related_case_study_url",
            as: "related_case_study_url",
            type: "string",
          },
          { field: "blog_post_title", as: "blog_post_title", type: "string" },
          { field: "blog_post_url", as: "blog_post_url", type: "string" },
          {
            field: "feature_highlight",
            as: "feature_highlight",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "personalized_cta", as: "personalized_cta", type: "string" },
          { field: "follow_up_angle", as: "follow_up_angle", type: "string" },
        ],
      ),

      // 5. Follow-up email 1 — social proof
      emailNode(
        "5",
        "Send Social Proof Follow-Up Email",
        1000,
        "{lead_email}",
        "More proof that {feature_highlight} works for {lead_industry}",
        "<p>Hi {first_name},</p><p>We noticed you were reading our case study on <strong>{case_study_title}</strong>. Great choice — it is one of our most popular success stories.</p><p>If you found that interesting, you will love this related story:</p><p><strong><a href='{related_case_study_url}'>{related_case_study_title}</a></strong></p><p>{value_proposition}</p><p>We also published a deep dive on the same topic: <a href='{blog_post_url}'>{blog_post_title}</a></p><p>{personalized_cta}</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days Before Demo Offer", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after the social proof email to give the lead time to engage before offering a demo",
        },
      }),

      // 7. Demo offer email
      emailNode(
        "7",
        "Send Demo Offer Email",
        1480,
        "{lead_email}",
        "{first_name}, see {feature_highlight} in action?",
        "<p>Hi {first_name},</p><p>A few days ago we shared how companies in {case_study_industry} are getting results with {feature_highlight}. Companies like {company_name} in the {lead_industry} space see similar wins every day.</p><p>Want to see exactly how it would work for your team? We would love to give you a personalized 20-minute demo.</p><p><a href='https://calendly.com/your-team/demo?email={lead_email}&name={first_name}'>Book your demo now</a></p><p>{follow_up_angle}</p><p>Best,<br/>The Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1530. CASE-STUDY DRIP AFTER PRICING PAGE (8 nodes)
  // ===================================================================

  "Case-Study Drip After Pricing Page": {
    summary:
      "Fires when a lead visits the pricing page, extracts lead data with AI, loads their profile from HubSpot, uses AI to match the best case studies, sends an initial social proof email, waits 5 days, sends an objection-handling email, then checks if the lead revisited the pricing page via a condition to determine next steps.",
    nodes: [
      trigger("Webhook: Pricing page visited", "webhook_app", {
        source: "website_analytics",
        event: "page.pricing_viewed",
        howItStarts:
          "Fires when an identified lead visits the pricing page, indicating strong purchase intent — captured via tracking pixel, Segment, or CMS webhook",
        setup:
          "Configure website tracking to fire webhook on pricing page views with visitor identity. Set OpenAI API key for AI extraction and case study matching. Provide HubSpot private app token for lead data. Configure email credentials for drip sequence.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI Extract Pricing Page Visitor Data",
        280,
        "You are a marketing analytics parser. Extract the visitor email, name, the pricing page URL, time spent, scroll depth, any pricing tier they interacted with, referrer, and UTM data from the raw webhook payload. Respond JSON: {visitor_email: string, visitor_name: string, pricing_url: string, time_on_page_seconds: string, scroll_depth_pct: string, pricing_tier_viewed: string, referrer_url: string, utm_source: string, utm_campaign: string, visit_count: string}",
        "Raw webhook payload: {webhook_payload}. Extract all pricing page visit data regardless of field naming.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "pricing_url", as: "pricing_url", type: "string" },
          {
            field: "time_on_page_seconds",
            as: "time_on_page_seconds",
            type: "string",
          },
          { field: "scroll_depth_pct", as: "scroll_depth_pct", type: "string" },
          {
            field: "pricing_tier_viewed",
            as: "pricing_tier_viewed",
            type: "string",
          },
          { field: "referrer_url", as: "referrer_url", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "visit_count", as: "visit_count", type: "string" },
        ],
      ),

      // 3. Lead profile loader HubSpot
      hubspotGetNode(
        "3",
        "Load Lead Profile from HubSpot",
        520,
        "crm/v3/objects/contacts/{visitor_email}?idProperty=email&properties=firstname,lastname,company,industry,lifecyclestage,hs_lead_status,num_associated_deals",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "lead_industry",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. AI case study matcher
      aiNode(
        "4",
        "AI Match Best Case Studies for Lead",
        760,
        "You are a conversion optimization strategist. Given that this lead just visited pricing, select the most persuasive case studies to address likely objections. Consider their industry, company size signals, and the pricing tier they viewed. Pick a primary case study for ROI proof, a secondary one for objection handling, and craft specific messaging that bridges from pricing to value. Respond JSON: {primary_case_study: string, primary_case_study_url: string, primary_roi_stat: string, secondary_case_study: string, secondary_case_study_url: string, objection_addressed: string, pricing_tier_value_prop: string, urgency_message: string}",
        "Lead: {first_name} {last_name} at {company_name} ({lead_industry}). Stage: {lifecycle_stage}. Pricing tier viewed: {pricing_tier_viewed}. Time on pricing: {time_on_page_seconds}s. Scroll depth: {scroll_depth_pct}%. Visit count: {visit_count}. Select the most compelling case studies to overcome pricing hesitation.",
        [
          {
            field: "primary_case_study",
            as: "primary_case_study",
            type: "string",
          },
          {
            field: "primary_case_study_url",
            as: "primary_case_study_url",
            type: "string",
          },
          { field: "primary_roi_stat", as: "primary_roi_stat", type: "string" },
          {
            field: "secondary_case_study",
            as: "secondary_case_study",
            type: "string",
          },
          {
            field: "secondary_case_study_url",
            as: "secondary_case_study_url",
            type: "string",
          },
          {
            field: "objection_addressed",
            as: "objection_addressed",
            type: "string",
          },
          {
            field: "pricing_tier_value_prop",
            as: "pricing_tier_value_prop",
            type: "string",
          },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),

      // 5. Email 1 — social proof
      emailNode(
        "5",
        "Send Social Proof Email (ROI Focused)",
        1000,
        "{visitor_email}",
        "{first_name}, here is how {lead_industry} teams get {primary_roi_stat}",
        "<p>Hi {first_name},</p><p>We saw you checking out our pricing — and we totally get it, the investment matters. That is why we want to share some proof.</p><p><strong><a href='{primary_case_study_url}'>{primary_case_study}</a></strong></p><p>Key result: <strong>{primary_roi_stat}</strong></p><p>{pricing_tier_value_prop}</p><p>Worth a 5-minute read before making your decision.</p><p>Best,<br/>The Team</p>",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days Before Objection Email", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after the ROI proof email before sending objection-handling content",
        },
      }),

      // 7. Email 2 — objection handler
      emailNode(
        "7",
        "Send Objection-Handling Email",
        1480,
        "{visitor_email}",
        "Worried about {objection_addressed}? {company_name} is not alone",
        "<p>Hi {first_name},</p><p>When evaluating new tools, most {lead_industry} teams have the same concern: <strong>{objection_addressed}</strong>.</p><p>Here is how one team handled it:</p><p><strong><a href='{secondary_case_study_url}'>{secondary_case_study}</a></strong></p><p>The bottom line? {urgency_message}</p><p>Ready to talk specifics? <a href='https://calendly.com/your-team/demo?email={visitor_email}'>Let us set up a quick call</a>.</p><p>Best,<br/>The Team</p>",
      ),

      // 8. Pricing page revisit detector condition
      n("8", "condition", "Did Lead Revisit Pricing Page?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "visit_count", operator: "gte", value: "2" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1531. CERTIFICATION EXPIRY REMINDER (7 nodes)
  // ===================================================================

  "Certification Expiry Reminder": {
    summary:
      "Runs daily on schedule, queries a certification database from Google Sheets, classifies expiry windows with runCode, generates personalized renewal guidance with AI, sends a reminder email, checks if the manager should be notified via condition, and logs compliance status to Sheets.",
    nodes: [
      trigger("Schedule: Daily certification check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to scan the certification database and identify certifications expiring within 30, 60, or 90 days — covering professional licenses, compliance certs, safety training, and vendor certifications",
        setup:
          "Set Google Sheets API credentials and spreadsheet ID for the certification database. Set OpenAI API key for renewal guidance generation. Configure email credentials for reminders. Provide manager notification rules.",
        inputFields: [],
      }),

      // 2. Certification database query Sheets
      n("2", "hitApi", "Query Certification Database from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Certifications!A2:J500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "cert_records", type: "string" },
            { name: "range", customName: "data_range", type: "string" },
          ],
        },
      }),

      // 3. Expiry window classifier runCode
      n("3", "runCode", "Classify Certification Expiry Windows", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse certification records and classify by expiry urgency
const records = JSON.parse(vars.cert_records || "[]");
const now = new Date();
const results = [];
let urgent = 0, warning = 0, upcoming = 0;
for (const row of records) {
  if (!row || row.length < 6) continue;
  const name = row[0], email = row[1], certName = row[2], expiryStr = row[3], managerEmail = row[4], certType = row[5];
  const expiry = new Date(expiryStr);
  const daysLeft = Math.floor((expiry - now) / 86400000);
  if (daysLeft <= 0 || daysLeft > 90) continue;
  let urgency = "upcoming";
  if (daysLeft <= 30) { urgency = "urgent"; urgent++; }
  else if (daysLeft <= 60) { urgency = "warning"; warning++; }
  else { upcoming++; }
  results.push({ name, email, certName, expiryDate: expiryStr, daysLeft, urgency, managerEmail, certType });
}
const nextExpiring = results.length > 0 ? results[0] : { name: "none", email: "none", certName: "none", expiryDate: "none", daysLeft: 0, urgency: "none", managerEmail: "none", certType: "none" };
return {
  total_expiring: results.length,
  urgent_count: urgent,
  warning_count: warning,
  upcoming_count: upcoming,
  holder_name: nextExpiring.name,
  holder_email: nextExpiring.email,
  cert_name: nextExpiring.certName,
  expiry_date: nextExpiring.expiryDate,
  days_remaining: nextExpiring.daysLeft,
  urgency_level: nextExpiring.urgency,
  manager_email: nextExpiring.managerEmail,
  cert_type: nextExpiring.certType
};`,
          outputVariables: [
            { name: "total_expiring", type: "number" },
            { name: "urgent_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "upcoming_count", type: "number" },
            { name: "holder_name", type: "string" },
            { name: "holder_email", type: "string" },
            { name: "cert_name", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "days_remaining", type: "number" },
            { name: "urgency_level", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "cert_type", type: "string" },
          ],
        },
      }),

      // 4. AI renewal guide generator
      aiNode(
        "4",
        "AI Generate Renewal Guidance",
        760,
        "You are a compliance and certification specialist. Generate personalized renewal guidance for an employee whose certification is expiring. Include specific renewal steps for the certification type, estimated time to complete, any continuing education requirements, and the urgency of acting now. Respond JSON: {renewal_steps: string, estimated_time: string, ce_requirements: string, renewal_url: string, cost_estimate: string, consequences_of_lapse: string, recommended_action: string}",
        "Employee: {holder_name}. Certification: {cert_name} (Type: {cert_type}). Expiry date: {expiry_date}. Days remaining: {days_remaining}. Urgency: {urgency_level}. Generate specific, actionable renewal guidance.",
        [
          { field: "renewal_steps", as: "renewal_steps", type: "string" },
          { field: "estimated_time", as: "estimated_time", type: "string" },
          { field: "ce_requirements", as: "ce_requirements", type: "string" },
          { field: "renewal_url", as: "renewal_url", type: "string" },
          { field: "cost_estimate", as: "cost_estimate", type: "string" },
          {
            field: "consequences_of_lapse",
            as: "consequences_of_lapse",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      // 5. Reminder email
      emailNode(
        "5",
        "Send Certification Renewal Reminder",
        1000,
        "{holder_email}",
        "Action Required: Your {cert_name} expires in {days_remaining} days",
        "<h2>Certification Renewal Reminder</h2><p>Hi {holder_name},</p><p>Your <strong>{cert_name}</strong> certification expires on <strong>{expiry_date}</strong> — that is only <strong>{days_remaining} days</strong> away.</p><h3>Renewal Steps</h3><p>{renewal_steps}</p><h3>Details</h3><ul><li><strong>Estimated Time:</strong> {estimated_time}</li><li><strong>CE Requirements:</strong> {ce_requirements}</li><li><strong>Estimated Cost:</strong> {cost_estimate}</li></ul><p><strong>What happens if it lapses:</strong> {consequences_of_lapse}</p><p><strong>Recommended Action:</strong> {recommended_action}</p><p>Please start the renewal process as soon as possible.</p>",
      ),

      // 6. Manager notification condition
      n(
        "6",
        "condition",
        "Is Urgency Level Critical (30 days or less)?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "urgency_level", operator: "eq", value: "urgent" },
            ],
          },
        },
      ),

      // 7. Compliance tracker Sheets
      sheetsNode(
        "7",
        "Log Compliance Status to Sheets",
        1480,
        "ComplianceLog",
        '[["{holder_name}","{holder_email}","{cert_name}","{cert_type}","{expiry_date}","{days_remaining}","{urgency_level}","{recommended_action}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 1532. CERTIFICATION OR COURSE COMPLETION CELEBRATION (7 nodes)
  // ===================================================================

  "Certification or Course Completion Celebration": {
    summary:
      "Fires when a learner completes a course or certification via webhook, extracts achievement data with AI, enriches achievement context with runCode, generates a celebration message with AI, creates a digital certificate with runCode, sends a multi-channel celebration email, and sends a next-steps recommender email.",
    nodes: [
      trigger("Webhook: Course or certification completed", "webhook_app", {
        source: "lms_platform",
        event: "completion.achieved",
        howItStarts:
          "Fires when a learner completes a course, certification exam, or training program — from LMS platforms like Thinkific, Teachable, or internal training systems",
        setup:
          "Configure LMS webhook for completion events. Set OpenAI API key for celebration message and next-steps generation. Configure email credentials for celebration delivery.",
        inputFields: [],
      }),

      // 2. AI extract achievement data
      aiNode(
        "2",
        "AI Extract Achievement Data",
        280,
        "You are a learning management parser. Extract the learner name, email, course or certification title, completion date, score or grade, credential ID, issuing organization, and any badge or level earned from the raw webhook payload. Respond JSON: {learner_name: string, learner_email: string, course_title: string, completion_date: string, score_pct: string, credential_id: string, issuing_org: string, badge_earned: string, course_category: string, hours_completed: string, instructor_name: string}",
        "Raw LMS webhook payload: {webhook_payload}. Extract all achievement and completion data regardless of field naming.",
        [
          { field: "learner_name", as: "learner_name", type: "string" },
          { field: "learner_email", as: "learner_email", type: "string" },
          { field: "course_title", as: "course_title", type: "string" },
          { field: "completion_date", as: "completion_date", type: "string" },
          { field: "score_pct", as: "score_pct", type: "string" },
          { field: "credential_id", as: "credential_id", type: "string" },
          { field: "issuing_org", as: "issuing_org", type: "string" },
          { field: "badge_earned", as: "badge_earned", type: "string" },
          { field: "course_category", as: "course_category", type: "string" },
          { field: "hours_completed", as: "hours_completed", type: "string" },
          { field: "instructor_name", as: "instructor_name", type: "string" },
        ],
      ),

      // 3. Achievement data enricher runCode
      n("3", "runCode", "Enrich Achievement Context", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build certificate and celebration metadata
const score = parseFloat(vars.score_pct || "0");
const hours = parseFloat(vars.hours_completed || "0");
const category = (vars.course_category || "general").toLowerCase();
let honorLevel = "completion";
if (score >= 95) honorLevel = "distinction";
else if (score >= 90) honorLevel = "honors";
else if (score >= 80) honorLevel = "merit";
const certNumber = "CERT-" + Date.now().toString(36).toUpperCase();
const validUntil = new Date(Date.now() + 365 * 86400000).toISOString().split("T")[0];
const shareUrl = "https://credentials.example.com/verify/" + certNumber;
return {
  honor_level: honorLevel,
  certificate_number: certNumber,
  valid_until: validUntil,
  share_url: shareUrl,
  learning_hours: hours,
  achievement_summary: vars.course_title + " completed with " + score + "% (" + honorLevel + ")",
  linkedin_share_text: "I just earned my " + vars.course_title + " certification from " + vars.issuing_org + "! #certification #learning"
};`,
          outputVariables: [
            { name: "honor_level", type: "string" },
            { name: "certificate_number", type: "string" },
            { name: "valid_until", type: "string" },
            { name: "share_url", type: "string" },
            { name: "learning_hours", type: "number" },
            { name: "achievement_summary", type: "string" },
            { name: "linkedin_share_text", type: "string" },
          ],
        },
      }),

      // 4. AI celebration message generator
      aiNode(
        "4",
        "AI Generate Celebration Message",
        760,
        "You are a learning community manager who crafts heartfelt celebration messages. Generate a warm, professional congratulations message for a learner who just completed a course or certification. Include recognition of the effort, the specific achievement, and encouragement to share it. Respond JSON: {headline: string, congratulations_body: string, social_share_cta: string, fun_fact: string}",
        "Learner: {learner_name}. Course: {course_title}. Score: {score_pct}%. Honor Level: {honor_level}. Hours: {hours_completed}. Category: {course_category}. Issuing Org: {issuing_org}. Badge: {badge_earned}. Create a genuinely celebratory message.",
        [
          { field: "headline", as: "celebration_headline", type: "string" },
          {
            field: "congratulations_body",
            as: "congratulations_body",
            type: "string",
          },
          { field: "social_share_cta", as: "social_share_cta", type: "string" },
          { field: "fun_fact", as: "fun_fact", type: "string" },
        ],
      ),

      // 5. Digital certificate generator runCode
      n("5", "runCode", "Generate Digital Certificate Data", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build structured certificate payload for rendering
const cert = {
  recipient: vars.learner_name,
  title: vars.course_title,
  issuer: vars.issuing_org,
  date: vars.completion_date,
  score: vars.score_pct + "%",
  honor: vars.honor_level,
  certNumber: vars.certificate_number,
  validUntil: vars.valid_until,
  verifyUrl: vars.share_url,
  badge: vars.badge_earned,
  hours: vars.hours_completed + " hours"
};
const certJson = JSON.stringify(cert);
const certText = "Certificate of " + (vars.honor_level === "distinction" ? "Distinction" : vars.honor_level === "honors" ? "Honors" : "Completion");
return {
  certificate_payload: certJson,
  certificate_type_label: certText,
  verify_link: vars.share_url,
  cert_display_name: certText + " — " + vars.course_title
};`,
          outputVariables: [
            { name: "certificate_payload", type: "string" },
            { name: "certificate_type_label", type: "string" },
            { name: "verify_link", type: "string" },
            { name: "cert_display_name", type: "string" },
          ],
        },
      }),

      // 6. Multi-channel celebration email
      emailNode(
        "6",
        "Send Celebration Email with Certificate",
        1240,
        "{learner_email}",
        "{celebration_headline} — {course_title}",
        "<h1>{celebration_headline}</h1><p>Dear {learner_name},</p><p>{congratulations_body}</p><h2>{cert_display_name}</h2><p><strong>Certificate Number:</strong> {certificate_number}</p><p><strong>Score:</strong> {score_pct}% ({honor_level})</p><p><strong>Valid Until:</strong> {valid_until}</p><p><strong>Verify:</strong> <a href='{verify_link}'>{verify_link}</a></p><p>{fun_fact}</p><h3>Share Your Achievement</h3><p>{social_share_cta}</p><p>LinkedIn post suggestion: <em>{linkedin_share_text}</em></p>",
      ),

      // 7. Next steps recommender email
      emailNode(
        "7",
        "Send Next Steps Recommendation Email",
        1480,
        "{learner_email}",
        "What is next after {course_title}?",
        "<p>Hi {learner_name},</p><p>Now that you have earned your {cert_display_name}, here are some ways to build on your achievement:</p><ul><li>Explore advanced courses in {course_category}</li><li>Share your certificate on LinkedIn to boost your professional profile</li><li>Apply your new skills in a real project within the next 30 days</li><li>Mentor others who are starting the same {course_title} journey</li></ul><p>Your learning journey does not stop here. Keep growing!</p><p>Best,<br/>The Learning Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1533. CHAMPION LEFT ACCOUNT ALERT (8 nodes)
  // ===================================================================

  "Champion Left Account Alert": {
    summary:
      "Fires when a contact change is detected via webhook, extracts contact data with AI, validates departure via condition, assesses account risk in HubSpot, alerts the AE immediately via Slack, searches for replacement contacts via API, flags the CRM record for risk in HubSpot, and tracks the champion at their new company in Sheets.",
    nodes: [
      trigger("Webhook: Contact role change detected", "webhook_app", {
        source: "crm_enrichment",
        event: "contact.job_change",
        howItStarts:
          "Fires when an enrichment tool (Clearbit, ZoomInfo, LinkedIn Sales Nav) detects that a contact has changed roles, left their company, or updated their job title — especially critical when the contact is a champion or decision maker",
        setup:
          "Configure enrichment provider webhook for contact job change events. Set OpenAI API key for AI extraction. Provide HubSpot private app token for account assessment and CRM updates. Set Slack webhook URL for AE alerts. Configure Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI Extract Contact Change Data",
        280,
        "You are a CRM enrichment parser. Extract the contact name, email, previous company, previous title, new company, new title, change type (departure, promotion, lateral), the account they were associated with, and any internal champion or decision-maker flags from the raw webhook payload. Respond JSON: {contact_name: string, contact_email: string, previous_company: string, previous_title: string, new_company: string, new_title: string, change_type: string, account_name: string, account_id: string, was_champion: string, was_decision_maker: string, linkedin_url: string, change_detected_date: string}",
        "Raw enrichment webhook payload: {webhook_payload}. Extract all contact change data regardless of field naming.",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "previous_company", as: "previous_company", type: "string" },
          { field: "previous_title", as: "previous_title", type: "string" },
          { field: "new_company", as: "new_company", type: "string" },
          { field: "new_title", as: "new_title", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "was_champion", as: "was_champion", type: "string" },
          {
            field: "was_decision_maker",
            as: "was_decision_maker",
            type: "string",
          },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          {
            field: "change_detected_date",
            as: "change_detected_date",
            type: "string",
          },
        ],
      ),

      // 3. Departure validator condition
      n("3", "condition", "Was This Contact a Champion?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "was_champion", operator: "eq", value: "true" },
          ],
        },
      }),

      // 4. Account risk assessor HubSpot
      hubspotGetNode(
        "4",
        "Assess Account Risk in HubSpot",
        760,
        "crm/v3/objects/companies/{account_id}?properties=name,annualrevenue,num_associated_contacts,hs_lead_status,notes_last_contacted,hubspot_owner_id",
        [
          {
            name: "properties.name",
            customName: "hs_account_name",
            type: "string",
          },
          {
            name: "properties.annualrevenue",
            customName: "account_revenue",
            type: "string",
          },
          {
            name: "properties.num_associated_contacts",
            customName: "total_contacts",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "account_status",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "account_owner_id",
            type: "string",
          },
        ],
      ),

      // 5. Immediate AE alert Slack
      slackNode(
        "5",
        "Alert Account Executive via Slack",
        1000,
        "CHAMPION DEPARTURE ALERT\\n*{contact_name}* has left *{account_name}*\\nPrevious Role: {previous_title} at {previous_company}\\nNew Role: {new_title} at {new_company}\\nAccount Revenue: ${account_revenue}\\nRemaining Contacts: {total_contacts}\\nWas Decision Maker: {was_decision_maker}\\nLinkedIn: {linkedin_url}\\n\\nImmediate action required: identify replacement contact and assess renewal risk.",
      ),

      // 6. Replacement contact finder API
      n(
        "6",
        "hitApi",
        "Search for Replacement Contacts via Enrichment API",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.apollo.io/v1/mixed_people/search",
            headers: [
              { key: "Content-Type", value: "application/json" },
              { key: "Cache-Control", value: "no-cache" },
            ],
            body: [
              { key: "api_key", value: "{apollo_api_key}" },
              { key: "q_organization_name", value: "{account_name}" },
              { key: "person_titles", value: '["{previous_title}"]' },
              { key: "per_page", value: "5" },
            ],
            variables: [
              {
                name: "people[0].name",
                customName: "replacement_name",
                type: "string",
              },
              {
                name: "people[0].email",
                customName: "replacement_email",
                type: "string",
              },
              {
                name: "people[0].title",
                customName: "replacement_title",
                type: "string",
              },
              {
                name: "people[0].linkedin_url",
                customName: "replacement_linkedin",
                type: "string",
              },
              {
                name: "pagination.total_entries",
                customName: "replacement_candidates_found",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. CRM risk flag update HubSpot
      hubspotPostNode(
        "7",
        "Flag Account for Churn Risk in HubSpot",
        1480,
        "crm/v3/objects/companies/{account_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"AT_RISK","champion_departed":"true","champion_departure_date":"{change_detected_date}","departed_champion":"{contact_name}","replacement_contact_name":"{replacement_name}","replacement_contact_email":"{replacement_email}","notes_last_activity":"Champion {contact_name} departed to {new_company}. Replacement identified: {replacement_name} ({replacement_title}). Immediate outreach required."}',
          },
        ],
        [
          { name: "id", customName: "updated_company_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),

      // 8. Champion-at-new-company tracker Sheets
      sheetsNode(
        "8",
        "Track Champion at New Company",
        1720,
        "ChampionTracker",
        '[["{contact_name}","{contact_email}","{previous_company}","{previous_title}","{new_company}","{new_title}","{linkedin_url}","{change_detected_date}","{account_revenue}","pending_outreach"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1534. CHANNEL PARTNER ONBOARDING (9 nodes)
  // ===================================================================

  "Channel Partner Onboarding": {
    summary:
      "Fires when a new partner signs up via webhook, extracts partner data with AI, creates the partner profile in HubSpot, sends a welcome email, sets up portal and tools via API, assigns a training path email, sends a sales enablement kit email, introduces the partner manager via email, and tracks onboarding progress in Sheets.",
    nodes: [
      trigger("Webhook: New partner signup", "webhook_app", {
        source: "partner_portal",
        event: "partner.signup_completed",
        howItStarts:
          "Fires when a new channel partner, reseller, or affiliate completes their partner application and is approved — kicking off the full onboarding sequence",
        setup:
          "Configure partner portal webhook for signup events. Set OpenAI API key for AI extraction. Provide HubSpot token for partner CRM. Configure partner portal API for tool setup. Set email credentials and Google Sheets for onboarding tracking.",
        inputFields: [],
      }),

      // 2. AI extract partner data
      aiNode(
        "2",
        "AI Extract Partner Signup Data",
        280,
        "You are a partner operations parser. Extract the partner company name, primary contact name, email, phone, partner type (reseller, affiliate, referral, technology), territory, expected deal volume, existing customer base size, and any certifications or specialties from the raw webhook payload. Respond JSON: {partner_company: string, contact_name: string, contact_email: string, contact_phone: string, partner_type: string, territory: string, expected_deal_volume: string, customer_base_size: string, specialties: string, partner_tier: string, website_url: string}",
        "Raw partner portal webhook payload: {webhook_payload}. Extract all partner signup data regardless of field naming.",
        [
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_phone", as: "contact_phone", type: "string" },
          { field: "partner_type", as: "partner_type", type: "string" },
          { field: "territory", as: "territory", type: "string" },
          {
            field: "expected_deal_volume",
            as: "expected_deal_volume",
            type: "string",
          },
          {
            field: "customer_base_size",
            as: "customer_base_size",
            type: "string",
          },
          { field: "specialties", as: "specialties", type: "string" },
          { field: "partner_tier", as: "partner_tier", type: "string" },
          { field: "website_url", as: "website_url", type: "string" },
        ],
      ),

      // 3. Partner profile builder HubSpot
      hubspotPostNode(
        "3",
        "Create Partner Profile in HubSpot",
        520,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{partner_company}","type":"PARTNER","partner_type":"{partner_type}","partner_tier":"{partner_tier}","territory":"{territory}","website":"{website_url}","phone":"{contact_phone}","description":"Channel partner. Type: {partner_type}. Specialties: {specialties}. Territory: {territory}. Expected volume: {expected_deal_volume}. Customer base: {customer_base_size}."}',
          },
        ],
        [
          { name: "id", customName: "hs_partner_id", type: "string" },
          {
            name: "properties.name",
            customName: "hs_partner_name",
            type: "string",
          },
        ],
      ),

      // 4. Welcome email
      emailNode(
        "4",
        "Send Partner Welcome Email",
        760,
        "{contact_email}",
        "Welcome to the partner program, {partner_company}!",
        "<h1>Welcome to Our Partner Program!</h1><p>Hi {contact_name},</p><p>We are thrilled to have <strong>{partner_company}</strong> join our partner ecosystem as a <strong>{partner_type}</strong> partner in the <strong>{territory}</strong> territory.</p><h2>What Happens Next</h2><ol><li><strong>Portal Access</strong> — Your partner portal account is being set up (details coming shortly)</li><li><strong>Training</strong> — We will send your personalized training path based on your specialties</li><li><strong>Sales Kit</strong> — A complete enablement package with collateral, battle cards, and demo credentials</li><li><strong>Partner Manager</strong> — Your dedicated partner manager will reach out to schedule a kickoff call</li></ol><p>We are excited to help you drive success in {territory}.</p><p>Best,<br/>Partner Operations Team</p>",
      ),

      // 5. Portal tool setup API
      n("5", "hitApi", "Provision Partner Portal Access", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.example.com/partner-portal/provision",
          headers: [
            { key: "Authorization", value: "Bearer {portal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "partner_name", value: "{partner_company}" },
            { key: "contact_email", value: "{contact_email}" },
            { key: "partner_type", value: "{partner_type}" },
            { key: "partner_tier", value: "{partner_tier}" },
            { key: "territory", value: "{territory}" },
          ],
          variables: [
            {
              name: "portal_url",
              customName: "partner_portal_url",
              type: "string",
            },
            {
              name: "temp_password",
              customName: "portal_temp_password",
              type: "string",
            },
            {
              name: "demo_environment_url",
              customName: "demo_env_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Training path assignment email
      emailNode(
        "6",
        "Send Training Path Assignment Email",
        1240,
        "{contact_email}",
        "Your partner training path is ready, {contact_name}",
        "<h2>Your Personalized Training Path</h2><p>Hi {contact_name},</p><p>Based on your {partner_type} partner role and specialties in <strong>{specialties}</strong>, we have assigned the following training modules:</p><h3>Required Courses</h3><ul><li>Product Fundamentals (2 hours)</li><li>Sales Methodology and Positioning (1.5 hours)</li><li>Partner Portal Walkthrough (45 min)</li><li>Demo Environment Training (1 hour)</li></ul><h3>Recommended for {partner_type} Partners</h3><ul><li>Competitive Positioning Workshop</li><li>ROI Calculator Training</li><li>Customer Success Handoff Process</li></ul><p><strong>Portal Access:</strong> <a href='{partner_portal_url}'>{partner_portal_url}</a></p><p><strong>Demo Environment:</strong> <a href='{demo_env_url}'>{demo_env_url}</a></p><p>Complete the required courses within 14 days to activate your deal registration privileges.</p>",
      ),

      // 7. Sales enablement kit email
      emailNode(
        "7",
        "Send Sales Enablement Kit Email",
        1480,
        "{contact_email}",
        "Your {partner_company} sales enablement kit",
        "<h2>Sales Enablement Kit</h2><p>Hi {contact_name},</p><p>Here is everything you need to start selling effectively:</p><h3>Included Materials</h3><ul><li><strong>Product One-Pager</strong> — Key features and differentiators</li><li><strong>Battle Cards</strong> — Competitive positioning against top 5 competitors</li><li><strong>ROI Calculator</strong> — Customizable spreadsheet for prospect conversations</li><li><strong>Case Studies</strong> — 5 vertical-specific customer success stories</li><li><strong>Demo Script</strong> — Step-by-step guide for product demonstrations</li><li><strong>Pricing Guide</strong> — Partner pricing tiers and margin structure</li></ul><p>All materials are available in your partner portal under Resources.</p><p><strong>Deal Registration:</strong> Use the portal to register deals and lock in your margins.</p><p>Best,<br/>Partner Enablement Team</p>",
      ),

      // 8. Partner manager introduction email
      emailNode(
        "8",
        "Send Partner Manager Introduction",
        1720,
        "{contact_email}",
        "Meet your dedicated partner manager, {contact_name}",
        "<p>Hi {contact_name},</p><p>I wanted to personally welcome <strong>{partner_company}</strong> to our partner program. As your dedicated partner manager, I will be your primary point of contact for everything from deal support to strategic planning.</p><p><strong>What I help with:</strong></p><ul><li>Deal registration and pipeline support</li><li>Joint marketing and co-selling opportunities</li><li>Technical pre-sales assistance</li><li>Quarterly business reviews</li><li>Escalation and executive sponsorship</li></ul><p>Let us schedule a 30-minute kickoff call this week to align on your goals for the {territory} territory. I have seen great success with {partner_type} partners who ramp within the first 60 days.</p><p>Looking forward to a productive partnership!</p>",
      ),

      // 9. Onboarding progress tracker Sheets
      sheetsNode(
        "9",
        "Log Partner Onboarding Progress",
        1960,
        "PartnerOnboarding",
        '[["{partner_company}","{contact_name}","{contact_email}","{partner_type}","{partner_tier}","{territory}","{specialties}","portal_provisioned","training_assigned","kit_sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1535. CHURN RISK DETECTOR (8 nodes)
  // ===================================================================

  "Churn Risk Detector": {
    summary:
      "Runs daily on schedule, collects usage metrics from HubSpot, analyzes trends with runCode, predicts churn probability with AI, classifies risk tier via condition, alerts the CS team via Slack, sends an automated save offer email, and logs risk data to a dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Daily churn risk scan", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to analyze customer usage metrics, detect declining engagement patterns, predict churn probability, and alert the customer success team before it is too late",
        setup:
          "Set HubSpot private app token for usage and account data. Set OpenAI API key for AI churn prediction. Configure Slack webhook URL for CS team alerts. Provide email credentials for save offers. Set Google Sheets spreadsheet ID for risk dashboard.",
        inputFields: [],
      }),

      // 2. Usage metrics collector HubSpot
      hubspotGetNode(
        "2",
        "Collect Customer Usage Metrics from HubSpot",
        280,
        "crm/v3/objects/contacts/search?properties=email,firstname,lastname,company,last_login_date,logins_last_30_days,feature_usage_score,support_tickets_open,nps_score,contract_renewal_date,mrr,hubspot_owner_id",
        [
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.last_login_date",
            customName: "last_login",
            type: "string",
          },
          {
            name: "results[0].properties.logins_last_30_days",
            customName: "logins_30d",
            type: "string",
          },
          {
            name: "results[0].properties.feature_usage_score",
            customName: "usage_score",
            type: "string",
          },
          {
            name: "results[0].properties.support_tickets_open",
            customName: "open_tickets",
            type: "string",
          },
          {
            name: "results[0].properties.nps_score",
            customName: "nps_score",
            type: "string",
          },
          {
            name: "results[0].properties.contract_renewal_date",
            customName: "renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.mrr",
            customName: "monthly_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "csm_owner_id",
            type: "string",
          },
        ],
      ),

      // 3. Trend analyzer runCode
      n("3", "runCode", "Analyze Usage Trends and Risk Signals", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute churn risk signals from usage metrics
const logins = parseInt(vars.logins_30d || "0");
const usageScore = parseFloat(vars.usage_score || "50");
const openTickets = parseInt(vars.open_tickets || "0");
const nps = parseInt(vars.nps_score || "7");
const lastLogin = vars.last_login ? new Date(vars.last_login) : new Date();
const daysSinceLogin = Math.floor((Date.now() - lastLogin.getTime()) / 86400000);
const renewalDate = vars.renewal_date ? new Date(vars.renewal_date) : null;
const daysToRenewal = renewalDate ? Math.floor((renewalDate.getTime() - Date.now()) / 86400000) : 999;
let riskScore = 0;
if (daysSinceLogin > 14) riskScore += 25;
else if (daysSinceLogin > 7) riskScore += 15;
if (logins < 5) riskScore += 20;
else if (logins < 10) riskScore += 10;
if (usageScore < 30) riskScore += 25;
else if (usageScore < 50) riskScore += 15;
if (openTickets > 3) riskScore += 15;
if (nps <= 6) riskScore += 15;
else if (nps <= 7) riskScore += 5;
const riskTier = riskScore >= 60 ? "critical" : riskScore >= 40 ? "high" : riskScore >= 20 ? "medium" : "low";
return {
  risk_score: riskScore,
  risk_tier: riskTier,
  days_since_login: daysSinceLogin,
  days_to_renewal: daysToRenewal,
  risk_factors: "Login gap: " + daysSinceLogin + "d | Logins 30d: " + logins + " | Usage: " + usageScore + " | Open tickets: " + openTickets + " | NPS: " + nps,
  is_high_risk: riskScore >= 40 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "risk_score", type: "number" },
            { name: "risk_tier", type: "string" },
            { name: "days_since_login", type: "number" },
            { name: "days_to_renewal", type: "number" },
            { name: "risk_factors", type: "string" },
            { name: "is_high_risk", type: "string" },
          ],
        },
      }),

      // 4. AI churn predictor
      aiNode(
        "4",
        "AI Predict Churn and Recommend Intervention",
        760,
        "You are a customer success AI analyst. Given the customer usage metrics and risk signals, predict the churn probability, identify the primary churn driver, and recommend the most effective intervention strategy. Consider the customers MRR value and renewal timeline in your recommendations. Respond JSON: {churn_probability_pct: string, primary_driver: string, intervention_strategy: string, recommended_offer: string, talk_track: string, escalation_needed: string, save_confidence: string}",
        "Customer: {first_name} {last_name} at {company_name}. MRR: ${monthly_revenue}. Risk score: {risk_score} ({risk_tier}). Risk factors: {risk_factors}. Days to renewal: {days_to_renewal}. NPS: {nps_score}. Predict churn probability and recommend the best save strategy.",
        [
          {
            field: "churn_probability_pct",
            as: "churn_probability",
            type: "string",
          },
          {
            field: "primary_driver",
            as: "primary_churn_driver",
            type: "string",
          },
          {
            field: "intervention_strategy",
            as: "intervention_strategy",
            type: "string",
          },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          { field: "talk_track", as: "talk_track", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          { field: "save_confidence", as: "save_confidence", type: "string" },
        ],
      ),

      // 5. Risk tier classifier condition
      n("5", "condition", "Is Risk Tier High or Critical?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_risk", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. CS team alert Slack
      slackNode(
        "6",
        "Alert CS Team of High-Risk Account",
        1240,
        "CHURN RISK ALERT ({risk_tier})\\n*{first_name} {last_name}* at *{company_name}*\\nMRR: ${monthly_revenue} | Churn Probability: {churn_probability}%\\nPrimary Driver: {primary_churn_driver}\\nRisk Score: {risk_score} | NPS: {nps_score}\\nDays Since Login: {days_since_login} | Days to Renewal: {days_to_renewal}\\n\\nRecommended Strategy: {intervention_strategy}\\nTalk Track: {talk_track}\\nEscalation Needed: {escalation_needed}",
        300,
      ),

      // 7. Automated save offer email
      emailNode(
        "7",
        "Send Automated Save Offer Email",
        1240,
        "{customer_email}",
        "We miss you, {first_name} — here is something special",
        "<p>Hi {first_name},</p><p>We noticed it has been a while since you logged in, and we wanted to make sure everything is okay with your experience.</p><p>Your success with our platform matters to us. Here is what we would like to offer:</p><p><strong>{recommended_offer}</strong></p><p>We would also love to set up a quick call to make sure you are getting the most out of the platform. Our team can walk you through some new features that might help with your workflow.</p><p><a href='https://calendly.com/cs-team/check-in?email={customer_email}'>Schedule a 15-minute check-in</a></p><p>We are here to help.</p><p>Best,<br/>Customer Success Team</p>",
        500,
      ),

      // 8. Risk dashboard Sheets
      sheetsNode(
        "8",
        "Log Risk Data to Dashboard",
        1480,
        "ChurnRiskDashboard",
        '[["{first_name} {last_name}","{company_name}","{customer_email}","{monthly_revenue}","{risk_score}","{risk_tier}","{churn_probability}","{primary_churn_driver}","{intervention_strategy}","{days_to_renewal}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 1536. CLOSED-LOST REASON CAPTURE PLUS RECYCLE (8 nodes)
  // ===================================================================

  "Closed-Lost Reason Capture Plus Recycle": {
    summary:
      "Fires when a deal is marked closed-lost via webhook, extracts deal data with AI, sends a reason-capture survey email, waits 3 days for response, analyzes the loss with AI, enriches the CRM record in HubSpot, routes the lead to a recycle path via condition, and sends product feedback to Slack.",
    nodes: [
      trigger("Webhook: Deal closed-lost", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal is moved to closed-lost stage in HubSpot — triggering the loss analysis, feedback collection, and lead recycling workflow",
        setup:
          "Configure HubSpot workflow or webhook subscription for deal stage changes to closed-lost. Set OpenAI API key for AI analysis. Provide HubSpot token for CRM enrichment. Configure email for survey and Slack for product feedback.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI Extract Closed-Lost Deal Data",
        280,
        "You are a CRM deal parser. Extract the deal name, amount, close date, associated contact name and email, company name, deal stage history, assigned rep, pipeline name, product line, and any notes from the raw webhook payload. Respond JSON: {deal_name: string, deal_amount: string, close_date: string, contact_name: string, contact_email: string, company_name: string, deal_owner: string, pipeline_name: string, product_line: string, deal_age_days: string, competitor_mentioned: string, last_activity_note: string}",
        "Raw HubSpot deal webhook payload: {webhook_payload}. Extract all deal data regardless of field naming.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_owner", as: "deal_owner", type: "string" },
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
          { field: "product_line", as: "product_line", type: "string" },
          { field: "deal_age_days", as: "deal_age_days", type: "string" },
          {
            field: "competitor_mentioned",
            as: "competitor_mentioned",
            type: "string",
          },
          {
            field: "last_activity_note",
            as: "last_activity_note",
            type: "string",
          },
        ],
      ),

      // 3. Reason capture survey email
      emailNode(
        "3",
        "Send Loss Reason Survey Email",
        520,
        "{contact_email}",
        "Quick question about your evaluation, {contact_name}",
        "<p>Hi {contact_name},</p><p>We understand you have decided to go a different direction, and we completely respect that. We are always looking to improve, and your feedback would mean a lot to us.</p><p>Would you mind sharing what influenced your decision? Just hit reply with a quick note, or click the most relevant reason:</p><ul><li><a href='https://feedback.example.com/lost?deal={deal_name}&reason=pricing'>Pricing did not fit our budget</a></li><li><a href='https://feedback.example.com/lost?deal={deal_name}&reason=features'>Missing features we needed</a></li><li><a href='https://feedback.example.com/lost?deal={deal_name}&reason=competitor'>Went with a competitor</a></li><li><a href='https://feedback.example.com/lost?deal={deal_name}&reason=timing'>Timing was not right</a></li><li><a href='https://feedback.example.com/lost?deal={deal_name}&reason=internal'>Internal priorities changed</a></li></ul><p>Your honest feedback helps us build a better product for teams like yours.</p><p>Thank you,<br/>The Team</p>",
      ),

      // 4. Delay 3 days
      n("4", "delayAction", "Wait 3 Days for Survey Response", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days for the prospect to respond to the loss reason survey before proceeding with analysis",
        },
      }),

      // 5. AI loss analysis
      aiNode(
        "5",
        "AI Analyze Loss Patterns",
        1000,
        "You are a sales operations analyst. Analyze the closed-lost deal data to identify the likely primary loss reason, contributing factors, what could have been done differently, whether this deal should be recycled in the future, and product feedback to share with engineering. Respond JSON: {primary_loss_reason: string, contributing_factors: string, improvement_suggestions: string, recycle_eligible: string, recycle_timeframe: string, product_feedback: string, competitive_insight: string, deal_quality_score: string}",
        "Deal: {deal_name} worth ${deal_amount}. Company: {company_name}. Age: {deal_age_days} days. Pipeline: {pipeline_name}. Product: {product_line}. Competitor mentioned: {competitor_mentioned}. Last note: {last_activity_note}. Analyze this loss thoroughly and determine if recyclable.",
        [
          {
            field: "primary_loss_reason",
            as: "primary_loss_reason",
            type: "string",
          },
          {
            field: "contributing_factors",
            as: "contributing_factors",
            type: "string",
          },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
          { field: "recycle_eligible", as: "recycle_eligible", type: "string" },
          {
            field: "recycle_timeframe",
            as: "recycle_timeframe",
            type: "string",
          },
          { field: "product_feedback", as: "product_feedback", type: "string" },
          {
            field: "competitive_insight",
            as: "competitive_insight",
            type: "string",
          },
          {
            field: "deal_quality_score",
            as: "deal_quality_score",
            type: "string",
          },
        ],
      ),

      // 6. CRM enrichment HubSpot
      hubspotPostNode(
        "6",
        "Enrich CRM with Loss Analysis",
        1240,
        "crm/v3/objects/deals/{deal_name}",
        [
          {
            key: "properties",
            value:
              '{"closed_lost_reason":"{primary_loss_reason}","loss_contributing_factors":"{contributing_factors}","recycle_eligible":"{recycle_eligible}","recycle_timeframe":"{recycle_timeframe}","competitive_intelligence":"{competitive_insight}","deal_quality_score":"{deal_quality_score}","loss_analysis_date":"' +
              new Date().toISOString().split("T")[0] +
              '"}',
          },
        ],
        [
          { name: "id", customName: "enriched_deal_id", type: "string" },
          {
            name: "properties.closed_lost_reason",
            customName: "logged_reason",
            type: "string",
          },
        ],
      ),

      // 7. Recycle path router condition
      n("7", "condition", "Is Deal Eligible for Recycling?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "recycle_eligible", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Product feedback loop Slack
      slackNode(
        "8",
        "Send Product Feedback to Engineering Slack",
        1720,
        "CLOSED-LOST PRODUCT FEEDBACK\\nDeal: *{deal_name}* (${deal_amount})\\nCompany: {company_name}\\nLoss Reason: {primary_loss_reason}\\nContributing Factors: {contributing_factors}\\nProduct Feedback: {product_feedback}\\nCompetitive Insight: {competitive_insight}\\nDeal Quality: {deal_quality_score}\\nRecycle Eligible: {recycle_eligible} ({recycle_timeframe})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1537. CLOSED-WON ONBOARDING HANDOFF (9 nodes)
  // ===================================================================

  "Closed-Won Onboarding Handoff": {
    summary:
      "Fires when a deal is closed-won via webhook, extracts deal data with AI, compiles deal context from HubSpot, generates an onboarding plan with AI, assigns CSM and implementation lead with runCode, sends the internal handoff email, sends a customer welcome email, schedules a kickoff meeting via Calendly API, and transfers CRM ownership in HubSpot.",
    nodes: [
      trigger("Webhook: Deal closed-won", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal moves to closed-won stage in HubSpot — initiating the sales-to-CS handoff, onboarding plan creation, and customer welcome sequence",
        setup:
          "Configure HubSpot webhook for deal stage changes to closed-won. Set OpenAI API key for onboarding plan generation. Provide HubSpot token for CRM operations. Configure email credentials. Set Calendly API key for meeting scheduling.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI Extract Closed-Won Deal Data",
        280,
        "You are a CRM deal parser. Extract the deal name, total amount, contract term, associated contact name and email, company name, products purchased, number of seats, implementation requirements, assigned sales rep, close date, and any custom deal properties from the raw webhook payload. Respond JSON: {deal_name: string, deal_amount: string, contract_term_months: string, contact_name: string, contact_email: string, company_name: string, products_purchased: string, seat_count: string, implementation_notes: string, sales_rep_name: string, sales_rep_email: string, close_date: string, deal_id: string}",
        "Raw HubSpot deal webhook payload: {webhook_payload}. Extract all closed-won deal data regardless of field naming.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          {
            field: "contract_term_months",
            as: "contract_term",
            type: "string",
          },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "products_purchased",
            as: "products_purchased",
            type: "string",
          },
          { field: "seat_count", as: "seat_count", type: "string" },
          {
            field: "implementation_notes",
            as: "implementation_notes",
            type: "string",
          },
          { field: "sales_rep_name", as: "sales_rep_name", type: "string" },
          { field: "sales_rep_email", as: "sales_rep_email", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
        ],
      ),

      // 3. Deal context compiler HubSpot
      hubspotGetNode(
        "3",
        "Load Full Deal Context from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,dealstage,pipeline,hs_priority,description,notes_last_activity",
        [
          {
            name: "properties.description",
            customName: "deal_description",
            type: "string",
          },
          {
            name: "properties.hs_priority",
            customName: "deal_priority",
            type: "string",
          },
          {
            name: "properties.notes_last_activity",
            customName: "last_sales_note",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
        ],
      ),

      // 4. AI onboarding plan generator
      aiNode(
        "4",
        "AI Generate Onboarding Plan",
        760,
        "You are an onboarding strategist. Based on the deal details, products purchased, seat count, and implementation notes, create a structured 90-day onboarding plan. Include key milestones, training schedule, integration requirements, success metrics, and a risk assessment. Respond JSON: {onboarding_phases: string, week1_milestones: string, week2_4_milestones: string, month2_3_milestones: string, training_schedule: string, integration_requirements: string, success_metrics: string, risk_assessment: string, estimated_go_live_days: string}",
        "Deal: {deal_name} worth ${deal_amount} ({contract_term} months). Company: {company_name}. Products: {products_purchased}. Seats: {seat_count}. Implementation notes: {implementation_notes}. Priority: {deal_priority}. Sales notes: {last_sales_note}. Create a comprehensive onboarding plan.",
        [
          {
            field: "onboarding_phases",
            as: "onboarding_phases",
            type: "string",
          },
          { field: "week1_milestones", as: "week1_milestones", type: "string" },
          {
            field: "week2_4_milestones",
            as: "week2_4_milestones",
            type: "string",
          },
          {
            field: "month2_3_milestones",
            as: "month2_3_milestones",
            type: "string",
          },
          {
            field: "training_schedule",
            as: "training_schedule",
            type: "string",
          },
          {
            field: "integration_requirements",
            as: "integration_requirements",
            type: "string",
          },
          { field: "success_metrics", as: "success_metrics", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "estimated_go_live_days",
            as: "estimated_go_live_days",
            type: "string",
          },
        ],
      ),

      // 5. CSM / implementation lead assigner runCode
      n("5", "runCode", "Assign CSM and Implementation Lead", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign CSM and implementation lead based on deal value and products
const amount = parseFloat(vars.deal_amount || "0");
const products = (vars.products_purchased || "").toLowerCase();
const seats = parseInt(vars.seat_count || "1");
let csmTier = "standard";
if (amount >= 100000) csmTier = "enterprise";
else if (amount >= 50000) csmTier = "mid_market";
const csmMap = {
  enterprise: { name: "Senior CSM", email: "enterprise-csm@company.com" },
  mid_market: { name: "Mid-Market CSM", email: "midmarket-csm@company.com" },
  standard: { name: "CSM Team", email: "csm@company.com" }
};
const csm = csmMap[csmTier];
const needsImplementation = products.includes("enterprise") || products.includes("integration") || seats > 50;
const implLead = needsImplementation ? { name: "Implementation Engineer", email: "impl@company.com" } : { name: csm.name, email: csm.email };
return {
  csm_name: csm.name,
  csm_email: csm.email,
  impl_lead_name: implLead.name,
  impl_lead_email: implLead.email,
  csm_tier: csmTier,
  needs_dedicated_impl: needsImplementation ? "yes" : "no",
  handoff_priority: amount >= 100000 ? "high" : "standard"
};`,
          outputVariables: [
            { name: "csm_name", type: "string" },
            { name: "csm_email", type: "string" },
            { name: "impl_lead_name", type: "string" },
            { name: "impl_lead_email", type: "string" },
            { name: "csm_tier", type: "string" },
            { name: "needs_dedicated_impl", type: "string" },
            { name: "handoff_priority", type: "string" },
          ],
        },
      }),

      // 6. Internal handoff email
      emailNode(
        "6",
        "Send Internal Handoff Email",
        1240,
        "{csm_email}",
        "[{handoff_priority}] New Closed-Won Handoff: {deal_name} (${deal_amount})",
        "<h2>Sales-to-CS Handoff</h2><p><strong>Deal:</strong> {deal_name}</p><p><strong>Amount:</strong> ${deal_amount} ({contract_term} months)</p><p><strong>Company:</strong> {company_name}</p><p><strong>Contact:</strong> {contact_name} ({contact_email})</p><p><strong>Products:</strong> {products_purchased}</p><p><strong>Seats:</strong> {seat_count}</p><p><strong>Sales Rep:</strong> {sales_rep_name} ({sales_rep_email})</p><h3>Implementation Details</h3><p>{implementation_notes}</p><p><strong>Dedicated Implementation:</strong> {needs_dedicated_impl}</p><p><strong>Implementation Lead:</strong> {impl_lead_name}</p><h3>Onboarding Plan</h3><p><strong>Phases:</strong> {onboarding_phases}</p><p><strong>Week 1:</strong> {week1_milestones}</p><p><strong>Weeks 2-4:</strong> {week2_4_milestones}</p><p><strong>Months 2-3:</strong> {month2_3_milestones}</p><p><strong>Est. Go-Live:</strong> {estimated_go_live_days} days</p><p><strong>Risk Assessment:</strong> {risk_assessment}</p>",
      ),

      // 7. Customer welcome email
      emailNode(
        "7",
        "Send Customer Welcome Email",
        1480,
        "{contact_email}",
        "Welcome aboard, {contact_name}! Let us get you started",
        "<h1>Welcome to the Team!</h1><p>Hi {contact_name},</p><p>Thank you for choosing us — we are thrilled to have <strong>{company_name}</strong> on board!</p><p>Your dedicated Customer Success Manager is <strong>{csm_name}</strong>, who will be your primary point of contact throughout your journey.</p><h2>Your Onboarding Roadmap</h2><p><strong>Week 1:</strong> {week1_milestones}</p><p><strong>Weeks 2-4:</strong> {week2_4_milestones}</p><p><strong>Training:</strong> {training_schedule}</p><h2>Next Step</h2><p>We will be sending you a kickoff meeting invite shortly. This 45-minute session will cover account setup, introductions, and your customized success plan.</p><p>In the meantime, feel free to reach out to {csm_name} at {csm_email} with any questions.</p><p>We are excited to help you succeed!</p>",
      ),

      // 8. Kickoff meeting scheduler Calendly API
      n("8", "hitApi", "Schedule Kickoff Meeting via Calendly", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{kickoff_event_type_uuid}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "kickoff_booking_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "calendly_event_owner",
              type: "string",
            },
          ],
        },
      }),

      // 9. CRM ownership transfer HubSpot
      hubspotPostNode(
        "9",
        "Transfer CRM Ownership to CSM",
        1960,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hubspot_owner_id":"{csm_owner_id}","dealstage":"onboarding","csm_assigned":"{csm_name}","csm_email":"{csm_email}","implementation_lead":"{impl_lead_name}","kickoff_booking_link":"{kickoff_booking_url}","onboarding_plan":"{onboarding_phases}","estimated_go_live":"{estimated_go_live_days} days"}',
          },
        ],
        [
          { name: "id", customName: "transferred_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "new_deal_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1538. COLD OUTREACH SEQUENCER (9 nodes)
  // ===================================================================

  "Cold Outreach Sequencer": {
    summary:
      "Fires when a prospect list is received via webhook, extracts prospect data with AI, enriches via Apollo API, personalizes messaging with AI, checks email deliverability with runCode, sends touchpoint 1 email, waits 3 days, monitors engagement via condition, and sends touchpoint 2 follow-up email.",
    nodes: [
      trigger("Webhook: Prospect list received", "webhook_app", {
        source: "sales_ops",
        event: "prospect_list.created",
        howItStarts:
          "Fires when a new prospect is added to the outreach queue — from a manually uploaded list, CRM trigger, or lead scoring threshold being met",
        setup:
          "Configure webhook from your sales operations tool or CRM lead scoring system. Set OpenAI API key for email personalization. Provide Apollo.io API key for enrichment. Configure email sending domain with proper SPF/DKIM/DMARC for deliverability.",
        inputFields: [],
      }),

      // 2. AI extract prospect data
      aiNode(
        "2",
        "AI Extract Prospect Data",
        280,
        "You are a sales data parser. Extract the prospect name, email, company, title, industry, company size, any known pain points, the lead source, and ICP fit indicators from the raw webhook payload. Respond JSON: {prospect_name: string, prospect_email: string, prospect_company: string, prospect_title: string, industry: string, company_size: string, known_pain_points: string, lead_source: string, icp_score: string, linkedin_url: string}",
        "Raw prospect list webhook payload: {webhook_payload}. Extract all prospect data regardless of field naming.",
        [
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          {
            field: "known_pain_points",
            as: "known_pain_points",
            type: "string",
          },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "icp_score", as: "icp_score", type: "string" },
          { field: "linkedin_url", as: "prospect_linkedin", type: "string" },
        ],
      ),

      // 3. Prospect enrichment Apollo API
      n("3", "hitApi", "Enrich Prospect via Apollo", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "email", value: "{prospect_email}" },
          ],
          variables: [
            {
              name: "person.name",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "person.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "person.organization.estimated_num_employees",
              customName: "enriched_headcount",
              type: "string",
            },
            {
              name: "person.organization.short_description",
              customName: "company_description",
              type: "string",
            },
            {
              name: "person.organization.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "person.seniority",
              customName: "seniority_level",
              type: "string",
            },
            {
              name: "person.departments",
              customName: "department",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI email personalizer
      aiNode(
        "4",
        "AI Personalize Outreach Messaging",
        760,
        "You are an elite SDR copywriter. Given the prospect enrichment data, craft two highly personalized cold email variations. The first should be a short, curiosity-driven opener (under 100 words). The second should be a value-first follow-up. Reference specific details about the prospects company, role, and industry. Never be generic. Respond JSON: {email1_subject: string, email1_body: string, email2_subject: string, email2_body: string, personalization_hook: string, value_prop_for_role: string, suggested_cta: string}",
        "Prospect: {enriched_name}, {enriched_title} at {enriched_company}. Industry: {enriched_industry}. Headcount: {enriched_headcount}. Company: {company_description}. Seniority: {seniority_level}. Department: {department}. Pain points: {known_pain_points}. Craft two razor-sharp personalized emails.",
        [
          { field: "email1_subject", as: "email1_subject", type: "string" },
          { field: "email1_body", as: "email1_body", type: "string" },
          { field: "email2_subject", as: "email2_subject", type: "string" },
          { field: "email2_body", as: "email2_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          {
            field: "value_prop_for_role",
            as: "value_prop_for_role",
            type: "string",
          },
          { field: "suggested_cta", as: "suggested_cta", type: "string" },
        ],
      ),

      // 5. Email warmup / deliverability check runCode
      n("5", "runCode", "Validate Email Deliverability", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate email format and apply sending rules
const email = (vars.prospect_email || "").toLowerCase().trim();
const domain = email.split("@")[1] || "";
const blockedDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"];
const isPersonalEmail = blockedDomains.includes(domain);
const isValidFormat = /^[^@]+@[^@]+\\.[^@]+$/.test(email);
const seniority = (vars.seniority_level || "").toLowerCase();
const isPriority = seniority === "c_suite" || seniority === "vp" || seniority === "director";
const sendWindow = new Date().getHours() >= 8 && new Date().getHours() <= 17;
return {
  email_valid: isValidFormat && !isPersonalEmail ? "yes" : "no",
  is_personal_email: isPersonalEmail ? "yes" : "no",
  is_priority_prospect: isPriority ? "yes" : "no",
  send_in_window: sendWindow ? "yes" : "no",
  email_domain: domain,
  deliverability_status: isValidFormat && !isPersonalEmail ? "cleared" : "blocked"
};`,
          outputVariables: [
            { name: "email_valid", type: "string" },
            { name: "is_personal_email", type: "string" },
            { name: "is_priority_prospect", type: "string" },
            { name: "send_in_window", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "deliverability_status", type: "string" },
          ],
        },
      }),

      // 6. Touchpoint 1 email
      emailNode(
        "6",
        "Send Touchpoint 1 Cold Email",
        1240,
        "{prospect_email}",
        "{email1_subject}",
        "<p>{email1_body}</p><p>{suggested_cta}</p>",
      ),

      // 7. Delay 3 days
      n("7", "delayAction", "Wait 3 Days Before Follow-Up", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after first touchpoint before sending follow-up to allow time for engagement",
        },
      }),

      // 8. Engagement monitor condition
      n("8", "condition", "Did Prospect Open Touchpoint 1?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "is_priority_prospect", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 9. Touchpoint 2 follow-up email
      emailNode(
        "9",
        "Send Touchpoint 2 Follow-Up Email",
        1960,
        "{prospect_email}",
        "{email2_subject}",
        "<p>{email2_body}</p><p>{value_prop_for_role}</p><p>{suggested_cta}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
    ],
  },

  // ===================================================================
  // 1539. COMMUNITY WEBINAR SERIES NURTURE (8 nodes)
  // ===================================================================

  "Community Webinar Series Nurture": {
    summary:
      "Fires when a webinar ends via webhook, extracts attendance data with AI, segments attendees with runCode, generates a recap with AI, sends a post-webinar recap email, waits 5 days, sends a resource deep-dive email, and sends a next webinar promotion email.",
    nodes: [
      trigger("Webhook: Webinar ended", "webhook_app", {
        source: "webinar_platform",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar session ends — from Zoom Webinar, GoToWebinar, Livestorm, or similar platforms — triggering the post-event nurture sequence",
        setup:
          "Configure webinar platform webhook for session end events. Set OpenAI API key for recap generation and content selection. Configure email credentials for nurture sequence delivery.",
        inputFields: [],
      }),

      // 2. AI extract attendance data
      aiNode(
        "2",
        "AI Extract Webinar Attendance Data",
        280,
        "You are a webinar analytics parser. Extract the webinar title, date, duration, total registrants, total attendees, average attendance duration, the host name, topic category, attendee email list (or first attendee for processing), engagement score, and any poll results from the raw webhook payload. Respond JSON: {webinar_title: string, webinar_date: string, duration_minutes: string, total_registrants: string, total_attendees: string, avg_attendance_minutes: string, host_name: string, topic_category: string, attendee_email: string, attendee_name: string, engagement_score: string, poll_results: string, recording_url: string}",
        "Raw webinar platform webhook payload: {webhook_payload}. Extract all webinar and attendance data regardless of field naming.",
        [
          { field: "webinar_title", as: "webinar_title", type: "string" },
          { field: "webinar_date", as: "webinar_date", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "string" },
          {
            field: "total_registrants",
            as: "total_registrants",
            type: "string",
          },
          { field: "total_attendees", as: "total_attendees", type: "string" },
          {
            field: "avg_attendance_minutes",
            as: "avg_attendance_minutes",
            type: "string",
          },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "string" },
          { field: "poll_results", as: "poll_results", type: "string" },
          { field: "recording_url", as: "recording_url", type: "string" },
        ],
      ),

      // 3. Attendee segmenter runCode
      n("3", "runCode", "Segment Attendee by Engagement Level", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment attendee based on engagement metrics
const engagementScore = parseFloat(vars.engagement_score || "0");
const avgMinutes = parseFloat(vars.avg_attendance_minutes || "0");
const totalMinutes = parseFloat(vars.duration_minutes || "60");
const attendancePct = totalMinutes > 0 ? Math.round((avgMinutes / totalMinutes) * 100) : 0;
let segment = "low_engagement";
if (engagementScore >= 80 || attendancePct >= 90) segment = "high_engagement";
else if (engagementScore >= 50 || attendancePct >= 60) segment = "medium_engagement";
const isHotLead = segment === "high_engagement" ? "yes" : "no";
const followUpPriority = segment === "high_engagement" ? "high" : segment === "medium_engagement" ? "medium" : "low";
return {
  attendee_segment: segment,
  attendance_pct: attendancePct,
  is_hot_lead: isHotLead,
  follow_up_priority: followUpPriority,
  segment_label: segment.replace("_", " ").replace(/\\b\\w/g, l => l.toUpperCase())
};`,
          outputVariables: [
            { name: "attendee_segment", type: "string" },
            { name: "attendance_pct", type: "number" },
            { name: "is_hot_lead", type: "string" },
            { name: "follow_up_priority", type: "string" },
            { name: "segment_label", type: "string" },
          ],
        },
      }),

      // 4. AI recap generator
      aiNode(
        "4",
        "AI Generate Webinar Recap",
        760,
        "You are a content marketing specialist. Generate a compelling webinar recap that includes key takeaways, interesting quotes or stats from the session, a summary of poll results, and recommended next resources based on the topic. Make it engaging enough that even non-attendees would want to read it. Respond JSON: {recap_headline: string, key_takeaways: string, memorable_stats: string, poll_insights: string, recommended_resource_1: string, recommended_resource_1_url: string, recommended_resource_2: string, recommended_resource_2_url: string, next_webinar_teaser: string}",
        "Webinar: {webinar_title}. Host: {host_name}. Topic: {topic_category}. Duration: {duration_minutes} min. Attendees: {total_attendees}/{total_registrants}. Poll results: {poll_results}. Attendee segment: {attendee_segment}. Generate an engaging recap with actionable takeaways.",
        [
          { field: "recap_headline", as: "recap_headline", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "memorable_stats", as: "memorable_stats", type: "string" },
          { field: "poll_insights", as: "poll_insights", type: "string" },
          {
            field: "recommended_resource_1",
            as: "resource_1_title",
            type: "string",
          },
          {
            field: "recommended_resource_1_url",
            as: "resource_1_url",
            type: "string",
          },
          {
            field: "recommended_resource_2",
            as: "resource_2_title",
            type: "string",
          },
          {
            field: "recommended_resource_2_url",
            as: "resource_2_url",
            type: "string",
          },
          {
            field: "next_webinar_teaser",
            as: "next_webinar_teaser",
            type: "string",
          },
        ],
      ),

      // 5. Post-webinar recap email
      emailNode(
        "5",
        "Send Post-Webinar Recap Email",
        1000,
        "{attendee_email}",
        "{recap_headline} — {webinar_title} Recap",
        "<h1>{recap_headline}</h1><p>Hi {attendee_name},</p><p>Thank you for joining <strong>{webinar_title}</strong> with {host_name}! Here is your recap:</p><h2>Key Takeaways</h2><p>{key_takeaways}</p><h2>By the Numbers</h2><p>{memorable_stats}</p><h2>What the Audience Thought</h2><p>{poll_insights}</p><p><strong>Missed something?</strong> <a href='{recording_url}'>Watch the full recording</a></p><p>See you at the next one!</p>",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 Days Before Deep-Dive", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after recap email to space out the nurture sequence and maintain engagement",
        },
      }),

      // 7. Resource deep-dive email
      emailNode(
        "7",
        "Send Resource Deep-Dive Email",
        1480,
        "{attendee_email}",
        "Go deeper on {topic_category}: handpicked resources for you",
        "<p>Hi {attendee_name},</p><p>After our webinar on <strong>{webinar_title}</strong>, we curated two resources that go deeper on the topics that resonated most:</p><h3>1. {resource_1_title}</h3><p><a href='{resource_1_url}'>Read now</a></p><h3>2. {resource_2_title}</h3><p><a href='{resource_2_url}'>Read now</a></p><p>These resources build directly on the takeaways from the session, so you can put what you learned into practice right away.</p><p>Best,<br/>The Content Team</p>",
      ),

      // 8. Next webinar promotion email
      emailNode(
        "8",
        "Send Next Webinar Promotion Email",
        1720,
        "{attendee_email}",
        "You are invited: our next session continues where {webinar_title} left off",
        "<p>Hi {attendee_name},</p><p>{next_webinar_teaser}</p><p>Based on your engagement with <strong>{webinar_title}</strong>, we think you would love our upcoming session. It picks up exactly where we left off and goes even deeper.</p><p><a href='https://webinar.example.com/register-next?email={attendee_email}&source=nurture'>Reserve your spot now</a></p><p>Seats are limited — register today to secure yours.</p><p>Best,<br/>The Webinar Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1540. COMPETITOR COMPARISON PAGE NURTURE (7 nodes)
  // ===================================================================

  "Competitor Comparison Page Nurture": {
    summary:
      "Fires when a lead visits a comparison page via webhook, extracts competitor intent with AI, selects differentiation content with AI, sends a key differentiators email, waits 5 days, sends a switching story email, and logs competitive intelligence to HubSpot.",
    nodes: [
      trigger("Webhook: Comparison page visited", "webhook_app", {
        source: "website_analytics",
        event: "page.comparison_viewed",
        howItStarts:
          "Fires when an identified lead visits a competitor comparison page (e.g., us-vs-competitor) on the website, indicating active evaluation and competitive shopping",
        setup:
          "Configure website tracking to fire webhook on comparison page visits with visitor identity. Set OpenAI API key for competitor intent analysis. Provide HubSpot token for competitive intel logging. Configure email credentials for nurture.",
        inputFields: [],
      }),

      // 2. AI extract competitor intent
      aiNode(
        "2",
        "AI Extract Competitor Intent",
        280,
        "You are a competitive intelligence analyst. Extract the visitor email, name, the competitor being compared, the comparison page URL, time on page, scroll depth, any specific feature comparisons viewed, referrer URL, and search keywords used to reach the page from the raw webhook payload. Respond JSON: {visitor_email: string, visitor_name: string, competitor_name: string, comparison_url: string, time_on_page_seconds: string, scroll_depth_pct: string, features_compared: string, referrer_url: string, search_keywords: string, visit_timestamp: string}",
        "Raw website analytics webhook payload: {webhook_payload}. Extract all competitor comparison page visit data regardless of field naming.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "competitor_name", as: "competitor_name", type: "string" },
          { field: "comparison_url", as: "comparison_url", type: "string" },
          {
            field: "time_on_page_seconds",
            as: "time_on_page_seconds",
            type: "string",
          },
          { field: "scroll_depth_pct", as: "scroll_depth_pct", type: "string" },
          {
            field: "features_compared",
            as: "features_compared",
            type: "string",
          },
          { field: "referrer_url", as: "referrer_url", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
          { field: "visit_timestamp", as: "visit_timestamp", type: "string" },
        ],
      ),

      // 3. AI differentiation content selector
      aiNode(
        "3",
        "AI Select Differentiation Content",
        520,
        "You are a competitive positioning expert. Given the specific competitor being evaluated and the features the prospect compared, craft the most compelling differentiation messaging. Highlight the strongest advantages over this specific competitor, a migration success story, and a specific feature deep-dive that showcases the biggest gap. Respond JSON: {top_differentiator_1: string, top_differentiator_2: string, top_differentiator_3: string, migration_story_title: string, migration_story_url: string, feature_deep_dive: string, feature_deep_dive_url: string, switching_cost_message: string, competitor_weakness: string}",
        "Competitor: {competitor_name}. Features compared: {features_compared}. Search keywords: {search_keywords}. Time on page: {time_on_page_seconds}s. Scroll depth: {scroll_depth_pct}%. Craft razor-sharp competitive positioning for this specific evaluation.",
        [
          {
            field: "top_differentiator_1",
            as: "differentiator_1",
            type: "string",
          },
          {
            field: "top_differentiator_2",
            as: "differentiator_2",
            type: "string",
          },
          {
            field: "top_differentiator_3",
            as: "differentiator_3",
            type: "string",
          },
          {
            field: "migration_story_title",
            as: "migration_story_title",
            type: "string",
          },
          {
            field: "migration_story_url",
            as: "migration_story_url",
            type: "string",
          },
          {
            field: "feature_deep_dive",
            as: "feature_deep_dive",
            type: "string",
          },
          {
            field: "feature_deep_dive_url",
            as: "feature_deep_dive_url",
            type: "string",
          },
          {
            field: "switching_cost_message",
            as: "switching_cost_message",
            type: "string",
          },
          {
            field: "competitor_weakness",
            as: "competitor_weakness",
            type: "string",
          },
        ],
      ),

      // 4. Email 1 — key differentiators
      emailNode(
        "4",
        "Send Key Differentiators Email",
        760,
        "{visitor_email}",
        "Comparing us to {competitor_name}? Here is what most people miss",
        "<p>Hi {visitor_name},</p><p>We noticed you were checking out our comparison with {competitor_name} — smart move doing your homework! Here are the three things that matter most:</p><ol><li><strong>{differentiator_1}</strong></li><li><strong>{differentiator_2}</strong></li><li><strong>{differentiator_3}</strong></li></ol><p>Want to go deeper? Check out this feature comparison: <a href='{feature_deep_dive_url}'>{feature_deep_dive}</a></p><p>Happy to jump on a quick call to answer any specific questions about your evaluation.</p><p>Best,<br/>The Team</p>",
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 Days Before Switching Story", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
          description:
            "Wait 5 days after differentiators email before sending the switching success story",
        },
      }),

      // 6. Email 2 — switching story
      emailNode(
        "6",
        "Send Switching Story Email",
        1240,
        "{visitor_email}",
        "How one team switched from {competitor_name} in under a week",
        "<p>Hi {visitor_name},</p><p>Still weighing your options? Here is a real story from a team that made the switch:</p><p><strong><a href='{migration_story_url}'>{migration_story_title}</a></strong></p><p>{switching_cost_message}</p><p>The biggest surprise for most teams switching from {competitor_name}? How quick and painless the migration actually is.</p><p><a href='https://calendly.com/your-team/demo?email={visitor_email}'>Book a 15-minute migration walkthrough</a></p><p>Best,<br/>The Team</p>",
      ),

      // 7. Competitive intel logger HubSpot
      hubspotPostNode(
        "7",
        "Log Competitive Intelligence to HubSpot",
        1480,
        "crm/v3/objects/contacts/{visitor_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"competitor_evaluated":"{competitor_name}","features_compared":"{features_compared}","comparison_page_viewed":"{comparison_url}","competitive_nurture_sent":"true","competitive_evaluation_date":"{visit_timestamp}"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.competitor_evaluated",
            customName: "logged_competitor",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1541. COMPLIANCE OR SECURITY DOC-REQUEST NURTURE (7 nodes)
  // ===================================================================

  "Compliance or Security Doc-Request Nurture": {
    summary:
      "Fires when a prospect requests security/compliance documentation via webhook, extracts request data with AI, classifies the request type via condition, immediately delivers the requested documents via email, sends a trust-building follow-up email, uses AI for security Q&A, and offers a security review meeting via Calendly API.",
    nodes: [
      trigger("Webhook: Security doc request received", "webhook_app", {
        source: "website_form",
        event: "form.security_docs_requested",
        howItStarts:
          "Fires when a prospect submits a request for security documentation, SOC 2 reports, compliance certifications, privacy policies, or data processing agreements via website form or sales conversation",
        setup:
          "Configure website form webhook for security document request submissions. Set OpenAI API key for request classification and Q&A. Configure email credentials for document delivery and nurture. Provide Calendly API key for meeting scheduling.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI Extract Doc Request Details",
        280,
        "You are a security and compliance document request parser. Extract the requestor name, email, company, role, the specific documents requested (SOC 2, ISO 27001, GDPR DPA, HIPAA BAA, penetration test, privacy policy, etc.), the urgency level, any specific compliance requirements mentioned, and the evaluation context from the raw webhook payload. Respond JSON: {requestor_name: string, requestor_email: string, requestor_company: string, requestor_role: string, documents_requested: string, request_urgency: string, compliance_framework: string, evaluation_context: string, industry: string, company_size: string}",
        "Raw form webhook payload: {webhook_payload}. Extract all security document request data regardless of field naming.",
        [
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          {
            field: "requestor_company",
            as: "requestor_company",
            type: "string",
          },
          { field: "requestor_role", as: "requestor_role", type: "string" },
          {
            field: "documents_requested",
            as: "documents_requested",
            type: "string",
          },
          { field: "request_urgency", as: "request_urgency", type: "string" },
          {
            field: "compliance_framework",
            as: "compliance_framework",
            type: "string",
          },
          {
            field: "evaluation_context",
            as: "evaluation_context",
            type: "string",
          },
          { field: "industry", as: "requestor_industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
        ],
      ),

      // 3. Request classifier condition
      n("3", "condition", "Is This an Urgent NDA-Required Request?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "request_urgency", operator: "eq", value: "urgent" },
          ],
        },
      }),

      // 4. Immediate document delivery email
      emailNode(
        "4",
        "Deliver Requested Security Documents",
        760,
        "{requestor_email}",
        "Your requested security documentation is ready, {requestor_name}",
        "<h2>Security Documentation Delivery</h2><p>Hi {requestor_name},</p><p>Thank you for your interest in our security posture. Here are the documents you requested:</p><h3>Requested: {documents_requested}</h3><ul><li><strong>SOC 2 Type II Report</strong> — <a href='https://trust.example.com/soc2'>Access via Trust Center</a></li><li><strong>ISO 27001 Certificate</strong> — <a href='https://trust.example.com/iso27001'>View Certificate</a></li><li><strong>Data Processing Agreement (DPA)</strong> — <a href='https://trust.example.com/dpa'>Download DPA</a></li><li><strong>Privacy Policy</strong> — <a href='https://example.com/privacy'>Read Privacy Policy</a></li><li><strong>Penetration Test Summary</strong> — <a href='https://trust.example.com/pentest'>Request Access</a> (NDA required)</li></ul><p>All public documents are available on our <a href='https://trust.example.com'>Trust Center</a>. For documents requiring NDA, our security team will follow up within 24 hours.</p><p>Best,<br/>Security & Compliance Team</p>",
      ),

      // 5. Trust-building follow-up email
      emailNode(
        "5",
        "Send Trust-Building Follow-Up Email",
        1000,
        "{requestor_email}",
        "Our security commitment to {requestor_company}",
        "<p>Hi {requestor_name},</p><p>Beyond the documents we shared, here is a quick overview of our security commitment that is relevant to {requestor_industry} organizations like {requestor_company}:</p><ul><li><strong>Encryption:</strong> AES-256 at rest, TLS 1.3 in transit</li><li><strong>Access Control:</strong> Role-based with SSO/SAML integration</li><li><strong>Data Residency:</strong> Configurable per your compliance requirements</li><li><strong>Incident Response:</strong> 24/7 monitoring with 1-hour SLA for critical alerts</li><li><strong>Compliance:</strong> SOC 2 Type II, ISO 27001, GDPR, CCPA compliant</li></ul><p>We take security as seriously as you do. If you have specific questions about {compliance_framework}, we are happy to connect you with our CISO.</p><p>Best,<br/>Security Team</p>",
      ),

      // 6. AI security Q&A
      aiNode(
        "6",
        "AI Prepare Security Q&A Brief",
        1240,
        "You are a security and compliance expert. Based on the requestors industry, company size, and compliance framework requirements, prepare a targeted FAQ that addresses the most common security questions for their specific context. Include data handling, access controls, incident response, and regulatory compliance answers. Respond JSON: {faq_q1: string, faq_a1: string, faq_q2: string, faq_a2: string, faq_q3: string, faq_a3: string, industry_specific_note: string, recommended_next_step: string}",
        "Requestor: {requestor_name} ({requestor_role}) at {requestor_company}. Industry: {requestor_industry}. Company size: {company_size}. Framework: {compliance_framework}. Documents requested: {documents_requested}. Evaluation context: {evaluation_context}. Prepare targeted security Q&A.",
        [
          { field: "faq_q1", as: "faq_q1", type: "string" },
          { field: "faq_a1", as: "faq_a1", type: "string" },
          { field: "faq_q2", as: "faq_q2", type: "string" },
          { field: "faq_a2", as: "faq_a2", type: "string" },
          { field: "faq_q3", as: "faq_q3", type: "string" },
          { field: "faq_a3", as: "faq_a3", type: "string" },
          {
            field: "industry_specific_note",
            as: "industry_security_note",
            type: "string",
          },
          {
            field: "recommended_next_step",
            as: "recommended_next_step",
            type: "string",
          },
        ],
      ),

      // 7. Security review meeting offer Calendly API
      n("7", "hitApi", "Offer Security Review Meeting via Calendly", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            {
              key: "owner",
              value:
                "https://api.calendly.com/event_types/{security_review_event_uuid}",
            },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "security_review_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "calendly_owner",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1542. CONTACT FORM TO CRM (7 nodes)
  // ===================================================================

  "Contact Form to CRM": {
    summary:
      "Fires when a contact form is submitted via webhook, validates and sanitizes input with runCode, checks for duplicates in HubSpot, creates or updates the CRM record in HubSpot, attributes lead source with runCode, sends an auto-response email, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: Contact form submitted", "webhook_form", {
        source: "website_form",
        event: "form.contact_submitted",
        howItStarts:
          "Fires when a visitor submits a contact form on the website — capturing lead information and routing it into the CRM with proper attribution, deduplication, and team notification",
        setup:
          "Configure website form to fire webhook on submission. Set HubSpot private app token for CRM operations. Configure email credentials for auto-response. Set Slack webhook URL for team notifications.",
        inputFields: [],
      }),

      // 2. Input validator/sanitizer runCode
      n("2", "runCode", "Validate and Sanitize Form Input", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and sanitize contact form webhook payload
const payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload;
const raw = payload || {};
const getName = (o) => o.name || o.full_name || o.first_name || (o.firstName + " " + (o.lastName || "")) || "Unknown";
const getEmail = (o) => (o.email || o.email_address || o.contact_email || "").toLowerCase().trim();
const getPhone = (o) => (o.phone || o.phone_number || o.tel || "").replace(/[^0-9+]/g, "");
const getMessage = (o) => o.message || o.inquiry || o.comments || o.body || "";
const getCompany = (o) => o.company || o.company_name || o.organization || "";
const name = getName(raw);
const email = getEmail(raw);
const phone = getPhone(raw);
const message = getMessage(raw).substring(0, 2000);
const company = getCompany(raw);
const isValidEmail = /^[^@]+@[^@]+\\.[^@]+$/.test(email);
const isSpam = message.toLowerCase().includes("buy now") || message.toLowerCase().includes("click here") || !isValidEmail;
return {
  contact_name: name,
  contact_email: email,
  contact_phone: phone,
  contact_message: message,
  contact_company: company,
  is_valid: !isSpam && isValidEmail ? "yes" : "no",
  submission_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "contact_name", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "contact_phone", type: "string" },
            { name: "contact_message", type: "string" },
            { name: "contact_company", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "submission_date", type: "string" },
          ],
        },
      }),

      // 3. Duplicate checker HubSpot
      hubspotGetNode(
        "3",
        "Check for Duplicate Contact in HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=firstname,lastname,company,lifecyclestage,hs_lead_status",
        [
          { name: "id", customName: "existing_contact_id", type: "string" },
          {
            name: "properties.firstname",
            customName: "existing_first_name",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "existing_lifecycle",
            type: "string",
          },
        ],
      ),

      // 4. CRM record handler HubSpot
      hubspotPostNode(
        "4",
        "Create or Update CRM Contact",
        760,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{contact_name}","phone":"{contact_phone}","company":"{contact_company}","hs_lead_status":"NEW","lifecyclestage":"lead","message":"{contact_message}","form_submission_date":"{submission_date}"}',
          },
        ],
        [
          { name: "id", customName: "crm_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "crm_created_date",
            type: "string",
          },
        ],
      ),

      // 5. Lead source attribution runCode
      n("5", "runCode", "Attribute Lead Source", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine lead source and quality from available signals
const email = (vars.contact_email || "").toLowerCase();
const domain = email.split("@")[1] || "";
const message = (vars.contact_message || "").toLowerCase();
const freeEmails = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com"];
const isBusinessEmail = !freeEmails.includes(domain);
let leadQuality = "standard";
if (isBusinessEmail && message.length > 50) leadQuality = "high";
else if (!isBusinessEmail) leadQuality = "low";
const hasUrgentKeywords = message.includes("asap") || message.includes("urgent") || message.includes("immediately") || message.includes("demo");
return {
  lead_source: "website_contact_form",
  is_business_email: isBusinessEmail ? "yes" : "no",
  email_domain: domain,
  lead_quality: leadQuality,
  is_urgent: hasUrgentKeywords ? "yes" : "no",
  attribution_notes: "Contact form | Domain: " + domain + " | Quality: " + leadQuality
};`,
          outputVariables: [
            { name: "lead_source", type: "string" },
            { name: "is_business_email", type: "string" },
            { name: "email_domain", type: "string" },
            { name: "lead_quality", type: "string" },
            { name: "is_urgent", type: "string" },
            { name: "attribution_notes", type: "string" },
          ],
        },
      }),

      // 6. Auto-response email
      emailNode(
        "6",
        "Send Auto-Response Email",
        1240,
        "{contact_email}",
        "Thanks for reaching out, {contact_name}!",
        "<p>Hi {contact_name},</p><p>Thank you for contacting us! We have received your message and a team member will get back to you within 24 hours.</p><p>Here is a copy of your message for reference:</p><blockquote>{contact_message}</blockquote><p>In the meantime, you might find these resources helpful:</p><ul><li><a href='https://example.com/faq'>Frequently Asked Questions</a></li><li><a href='https://example.com/docs'>Product Documentation</a></li><li><a href='https://example.com/blog'>Latest Blog Posts</a></li></ul><p>Best,<br/>The Team</p>",
      ),

      // 7. Team notification Slack
      slackNode(
        "7",
        "Notify Team of New Contact Form Submission",
        1480,
        "New Contact Form Submission\\n*{contact_name}* ({contact_email})\\nCompany: {contact_company}\\nQuality: {lead_quality} | Business Email: {is_business_email}\\nUrgent: {is_urgent}\\n\\nMessage: {contact_message}\\n\\nCRM ID: {crm_contact_id} | Source: {lead_source}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1543. CONTACT-US INQUIRY TRIAGE (8 nodes)
  // ===================================================================

  "Contact-Us Inquiry Triage": {
    summary:
      "Fires when a contact form is submitted via webhook, extracts inquiry data with AI, classifies intent with AI, filters spam via condition, assesses priority with runCode, routes to the correct department via condition, sends an auto-response email, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: Contact inquiry received", "webhook_form", {
        source: "website_form",
        event: "form.inquiry_submitted",
        howItStarts:
          "Fires when a visitor submits a contact-us inquiry — could be sales, support, partnership, press, or general questions requiring intelligent triage to the right team",
        setup:
          "Configure website contact form webhook. Set OpenAI API key for intent classification and spam detection. Configure email credentials for auto-response. Set Slack webhook URL for team routing.",
        inputFields: [],
      }),

      // 2. AI extract inquiry data
      aiNode(
        "2",
        "AI Extract Inquiry Data",
        280,
        "You are a customer inquiry parser. Extract the submitter name, email, company, phone, the inquiry message, any selected category or department from form fields, the page they submitted from, and their preferred contact method from the raw webhook payload. Respond JSON: {submitter_name: string, submitter_email: string, submitter_company: string, submitter_phone: string, inquiry_message: string, selected_category: string, source_page: string, preferred_contact: string, form_timestamp: string}",
        "Raw contact form webhook payload: {webhook_payload}. Extract all inquiry data regardless of field naming conventions.",
        [
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          {
            field: "submitter_company",
            as: "submitter_company",
            type: "string",
          },
          { field: "submitter_phone", as: "submitter_phone", type: "string" },
          { field: "inquiry_message", as: "inquiry_message", type: "string" },
          {
            field: "selected_category",
            as: "selected_category",
            type: "string",
          },
          { field: "source_page", as: "source_page", type: "string" },
          {
            field: "preferred_contact",
            as: "preferred_contact",
            type: "string",
          },
          { field: "form_timestamp", as: "form_timestamp", type: "string" },
        ],
      ),

      // 3. AI intent classifier
      aiNode(
        "3",
        "AI Classify Inquiry Intent",
        520,
        "You are a customer inquiry classification expert. Analyze the inquiry message and categorize it. Determine the intent (sales, support, partnership, press, careers, billing, general), the sentiment (positive, neutral, negative), whether it appears to be spam, and the complexity level (simple, moderate, complex). Respond JSON: {intent: string, sentiment: string, is_spam: string, complexity: string, department: string, suggested_response_template: string, key_topics: string}",
        "Submitter: {submitter_name} at {submitter_company}. Category selected: {selected_category}. Message: {inquiry_message}. Source page: {source_page}. Classify this inquiry accurately.",
        [
          { field: "intent", as: "inquiry_intent", type: "string" },
          { field: "sentiment", as: "inquiry_sentiment", type: "string" },
          { field: "is_spam", as: "is_spam", type: "string" },
          { field: "complexity", as: "inquiry_complexity", type: "string" },
          { field: "department", as: "target_department", type: "string" },
          {
            field: "suggested_response_template",
            as: "response_template",
            type: "string",
          },
          { field: "key_topics", as: "key_topics", type: "string" },
        ],
      ),

      // 4. Spam filter condition
      n("4", "condition", "Is This Inquiry Legitimate (Not Spam)?", 760, {
        conditionDetails: {
          conditions: [{ variable: "is_spam", operator: "eq", value: "false" }],
        },
      }),

      // 5. Priority assessor runCode
      n("5", "runCode", "Assess Inquiry Priority", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score inquiry priority based on signals
const intent = (vars.inquiry_intent || "general").toLowerCase();
const sentiment = (vars.inquiry_sentiment || "neutral").toLowerCase();
const complexity = (vars.inquiry_complexity || "simple").toLowerCase();
const company = vars.submitter_company || "";
const email = (vars.submitter_email || "").toLowerCase();
const domain = email.split("@")[1] || "";
const freeEmails = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"];
const isBusinessEmail = !freeEmails.includes(domain);
let priority = 0;
if (intent === "sales") priority += 30;
else if (intent === "support") priority += 25;
else if (intent === "partnership") priority += 20;
if (sentiment === "negative") priority += 20;
if (complexity === "complex") priority += 15;
if (isBusinessEmail) priority += 15;
if (company.length > 0) priority += 10;
const priorityLabel = priority >= 60 ? "high" : priority >= 35 ? "medium" : "low";
const slaHours = priorityLabel === "high" ? 2 : priorityLabel === "medium" ? 8 : 24;
return {
  priority_score: priority,
  priority_label: priorityLabel,
  sla_hours: slaHours,
  is_business_email: isBusinessEmail ? "yes" : "no",
  routing_summary: intent + " inquiry from " + (isBusinessEmail ? "business" : "personal") + " email | Priority: " + priorityLabel
};`,
          outputVariables: [
            { name: "priority_score", type: "number" },
            { name: "priority_label", type: "string" },
            { name: "sla_hours", type: "number" },
            { name: "is_business_email", type: "string" },
            { name: "routing_summary", type: "string" },
          ],
        },
      }),

      // 6. Department router condition
      n("6", "condition", "Is This a Sales Inquiry?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "inquiry_intent", operator: "eq", value: "sales" },
          ],
        },
      }),

      // 7. Auto-response email
      emailNode(
        "7",
        "Send Triage Auto-Response Email",
        1480,
        "{submitter_email}",
        "We received your inquiry, {submitter_name}",
        "<p>Hi {submitter_name},</p><p>Thank you for reaching out! We have received your message and routed it to our <strong>{target_department}</strong> team.</p><p>Based on your inquiry, here is what you can expect:</p><ul><li><strong>Response Time:</strong> Within {sla_hours} hours</li><li><strong>Team:</strong> {target_department}</li><li><strong>Reference:</strong> Your inquiry about: {key_topics}</li></ul><p>Here is a copy of your message:</p><blockquote>{inquiry_message}</blockquote><p>We will be in touch soon.</p><p>Best,<br/>The Team</p>",
      ),

      // 8. Team notification Slack
      slackNode(
        "8",
        "Notify Routed Team via Slack",
        1720,
        "New {inquiry_intent} Inquiry [{priority_label} priority]\\n*{submitter_name}* at {submitter_company} ({submitter_email})\\nSentiment: {inquiry_sentiment} | Complexity: {inquiry_complexity}\\nSLA: {sla_hours} hours\\nTopics: {key_topics}\\n\\nMessage: {inquiry_message}\\n\\nRouting: {routing_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1544. CONTENT RECOMMENDATION BY FUNNEL STAGE (8 nodes)
  // ===================================================================

  "Content Recommendation by Funnel Stage": {
    summary:
      "Fires when a prospect changes funnel stage via webhook, extracts prospect data with AI, loads their profile from HubSpot, maps stage-appropriate content with runCode, personalizes with AI, sends a content recommendation email, checks engagement via condition, and logs activity to HubSpot.",
    nodes: [
      trigger("Webhook: Funnel stage change detected", "webhook_app", {
        source: "marketing_automation",
        event: "contact.stage_changed",
        howItStarts:
          "Fires when a prospect transitions between funnel stages (awareness, consideration, decision, evaluation) — triggering stage-appropriate content delivery",
        setup:
          "Configure marketing automation webhook for lifecycle stage changes. Set OpenAI API key for content personalization. Provide HubSpot token for prospect data and activity logging. Configure email credentials for content delivery.",
        inputFields: [],
      }),

      // 2. AI extract prospect data
      aiNode(
        "2",
        "AI Extract Funnel Stage Change Data",
        280,
        "You are a marketing automation parser. Extract the prospect email, name, previous funnel stage, new funnel stage, the trigger event that caused the stage change, any lead score, the time in previous stage, and associated campaign from the raw webhook payload. Respond JSON: {prospect_email: string, prospect_name: string, previous_stage: string, new_stage: string, stage_trigger: string, lead_score: string, time_in_prev_stage_days: string, associated_campaign: string, company_name: string}",
        "Raw marketing automation webhook payload: {webhook_payload}. Extract all funnel stage change data regardless of field naming.",
        [
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "stage_trigger", as: "stage_trigger", type: "string" },
          { field: "lead_score", as: "lead_score", type: "string" },
          {
            field: "time_in_prev_stage_days",
            as: "days_in_prev_stage",
            type: "string",
          },
          {
            field: "associated_campaign",
            as: "associated_campaign",
            type: "string",
          },
          { field: "company_name", as: "company_name", type: "string" },
        ],
      ),

      // 3. Prospect profile loader HubSpot
      hubspotGetNode(
        "3",
        "Load Prospect Profile from HubSpot",
        520,
        "crm/v3/objects/contacts/{prospect_email}?idProperty=email&properties=firstname,lastname,company,industry,jobtitle,num_associated_deals,hs_analytics_num_page_views",
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "prospect_industry",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_page_views",
            customName: "page_views",
            type: "string",
          },
        ],
      ),

      // 4. Funnel stage content mapper runCode
      n("4", "runCode", "Map Stage-Appropriate Content", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map content recommendations based on funnel stage
const stage = (vars.new_stage || "awareness").toLowerCase();
const industry = (vars.prospect_industry || "general").toLowerCase();
const contentMap = {
  awareness: {
    content_type: "educational",
    piece_1: "Industry Trends Report 2024",
    piece_1_url: "https://example.com/resources/trends-report",
    piece_2: "Getting Started Guide",
    piece_2_url: "https://example.com/resources/getting-started",
    cta: "Learn more about solving common " + industry + " challenges"
  },
  consideration: {
    content_type: "comparison",
    piece_1: "Product Comparison Guide",
    piece_1_url: "https://example.com/resources/comparison",
    piece_2: "ROI Calculator",
    piece_2_url: "https://example.com/tools/roi-calculator",
    cta: "See how we compare and calculate your potential ROI"
  },
  decision: {
    content_type: "proof",
    piece_1: "Customer Case Studies",
    piece_1_url: "https://example.com/customers",
    piece_2: "Implementation Guide",
    piece_2_url: "https://example.com/resources/implementation",
    cta: "Read how teams like yours succeeded and plan your rollout"
  },
  evaluation: {
    content_type: "technical",
    piece_1: "Technical Documentation",
    piece_1_url: "https://example.com/docs",
    piece_2: "Security & Compliance Overview",
    piece_2_url: "https://example.com/security",
    cta: "Dive into the technical details and security posture"
  }
};
const content = contentMap[stage] || contentMap.awareness;
return {
  content_type: content.content_type,
  content_piece_1: content.piece_1,
  content_piece_1_url: content.piece_1_url,
  content_piece_2: content.piece_2,
  content_piece_2_url: content.piece_2_url,
  stage_cta: content.cta,
  stage_label: stage.charAt(0).toUpperCase() + stage.slice(1)
};`,
          outputVariables: [
            { name: "content_type", type: "string" },
            { name: "content_piece_1", type: "string" },
            { name: "content_piece_1_url", type: "string" },
            { name: "content_piece_2", type: "string" },
            { name: "content_piece_2_url", type: "string" },
            { name: "stage_cta", type: "string" },
            { name: "stage_label", type: "string" },
          ],
        },
      }),

      // 5. AI content personalizer
      aiNode(
        "5",
        "AI Personalize Content Recommendations",
        1000,
        "You are a content marketing personalization expert. Given the prospects profile, funnel stage, and mapped content pieces, craft a personalized introduction that connects the content to their specific situation. Reference their industry, role, and company. Make the email feel hand-picked, not automated. Respond JSON: {personalized_intro: string, content_1_teaser: string, content_2_teaser: string, personalized_ps: string}",
        "Prospect: {first_name} {last_name}, {job_title} at {company_name} ({prospect_industry}). New stage: {new_stage}. Lead score: {lead_score}. Page views: {page_views}. Content 1: {content_piece_1}. Content 2: {content_piece_2}. Personalize the content recommendations for maximum relevance.",
        [
          {
            field: "personalized_intro",
            as: "personalized_intro",
            type: "string",
          },
          { field: "content_1_teaser", as: "content_1_teaser", type: "string" },
          { field: "content_2_teaser", as: "content_2_teaser", type: "string" },
          { field: "personalized_ps", as: "personalized_ps", type: "string" },
        ],
      ),

      // 6. Content recommendation email
      emailNode(
        "6",
        "Send Content Recommendation Email",
        1240,
        "{prospect_email}",
        "Curated for you: {content_type} resources for the {stage_label} stage",
        "<p>Hi {first_name},</p><p>{personalized_intro}</p><h3>1. {content_piece_1}</h3><p>{content_1_teaser}</p><p><a href='{content_piece_1_url}'>Read now</a></p><h3>2. {content_piece_2}</h3><p>{content_2_teaser}</p><p><a href='{content_piece_2_url}'>Explore</a></p><p>{stage_cta}</p><p>P.S. {personalized_ps}</p>",
      ),

      // 7. Engagement tracker condition
      n("7", "condition", "Is Lead Score Above Threshold (50)?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "lead_score", operator: "gte", value: "50" },
          ],
        },
      }),

      // 8. CRM activity logger HubSpot
      hubspotPostNode(
        "8",
        "Log Content Delivery to HubSpot",
        1720,
        "crm/v3/objects/contacts/{prospect_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"last_content_sent":"{content_piece_1}, {content_piece_2}","last_content_stage":"{new_stage}","content_delivery_date":"' +
              new Date().toISOString().split("T")[0] +
              '","lead_score":"{lead_score}"}',
          },
        ],
        [
          { name: "id", customName: "logged_contact_id", type: "string" },
          {
            name: "properties.last_content_sent",
            customName: "logged_content",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1545. CONTENT RECOMMENDATION BY ROLE (7 nodes)
  // ===================================================================

  "Content Recommendation by Role": {
    summary:
      "Fires when a contact role is detected via webhook, extracts contact data with AI, classifies the role with runCode, loads a role-content matrix with runCode, curates content with AI, sends a personalized content email, and logs role engagement analytics to Sheets.",
    nodes: [
      trigger("Webhook: Contact role detected", "webhook_app", {
        source: "crm_enrichment",
        event: "contact.role_identified",
        howItStarts:
          "Fires when a contacts role/title is identified or updated through enrichment, form submission, or CRM update — triggering role-specific content delivery",
        setup:
          "Configure CRM or enrichment webhook for role identification events. Set OpenAI API key for content curation. Configure email credentials for content delivery. Set Google Sheets spreadsheet ID for analytics.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI Extract Contact and Role Data",
        280,
        "You are a contact enrichment parser. Extract the contact name, email, company, detected job title, department, seniority level, industry, and any interests or preferences from the raw webhook payload. Respond JSON: {contact_name: string, contact_email: string, company_name: string, job_title: string, department: string, seniority: string, industry: string, interests: string, detection_source: string}",
        "Raw CRM enrichment webhook payload: {webhook_payload}. Extract all contact and role data regardless of field naming.",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "seniority", as: "seniority", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          { field: "detection_source", as: "detection_source", type: "string" },
        ],
      ),

      // 3. Role classifier runCode
      n("3", "runCode", "Classify Role into Content Persona", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify job title into a content persona category
const title = (vars.job_title || "").toLowerCase();
const dept = (vars.department || "").toLowerCase();
const seniority = (vars.seniority || "").toLowerCase();
let persona = "individual_contributor";
if (title.includes("ceo") || title.includes("cto") || title.includes("cfo") || title.includes("coo") || title.includes("chief")) {
  persona = "c_suite";
} else if (title.includes("vp") || title.includes("vice president") || title.includes("svp")) {
  persona = "vp_executive";
} else if (title.includes("director") || title.includes("head of")) {
  persona = "director";
} else if (title.includes("manager") || title.includes("lead") || title.includes("team lead")) {
  persona = "manager";
} else if (title.includes("engineer") || title.includes("developer") || title.includes("architect")) {
  persona = "technical";
} else if (title.includes("analyst") || title.includes("operations") || title.includes("coordinator")) {
  persona = "operations";
}
const contentFocus = {
  c_suite: "strategy_and_roi",
  vp_executive: "business_impact",
  director: "implementation_and_results",
  manager: "productivity_and_tools",
  technical: "architecture_and_integration",
  operations: "process_and_efficiency",
  individual_contributor: "best_practices"
};
return {
  role_persona: persona,
  content_focus: contentFocus[persona] || "best_practices",
  persona_label: persona.replace(/_/g, " ").replace(/\\b\\w/g, l => l.toUpperCase()),
  is_decision_maker: ["c_suite", "vp_executive", "director"].includes(persona) ? "yes" : "no"
};`,
          outputVariables: [
            { name: "role_persona", type: "string" },
            { name: "content_focus", type: "string" },
            { name: "persona_label", type: "string" },
            { name: "is_decision_maker", type: "string" },
          ],
        },
      }),

      // 4. Role-content matrix loader runCode
      n("4", "runCode", "Load Role-Content Matrix", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map persona to specific content recommendations
const persona = vars.role_persona || "individual_contributor";
const industry = (vars.industry || "general").toLowerCase();
const matrix = {
  c_suite: {
    piece_1: "Executive Guide: Digital Transformation ROI",
    url_1: "https://example.com/resources/exec-guide",
    piece_2: "Board-Ready Business Case Template",
    url_2: "https://example.com/resources/business-case",
    piece_3: "CEO Peer Roundtable Insights",
    url_3: "https://example.com/resources/peer-insights"
  },
  vp_executive: {
    piece_1: "VP Playbook: Scaling Operations with Automation",
    url_1: "https://example.com/resources/vp-playbook",
    piece_2: "Impact Report: Before & After Metrics",
    url_2: "https://example.com/resources/impact-report",
    piece_3: "Executive Dashboard Setup Guide",
    url_3: "https://example.com/resources/exec-dashboard"
  },
  director: {
    piece_1: "Implementation Roadmap for Directors",
    url_1: "https://example.com/resources/impl-roadmap",
    piece_2: "Team Productivity Benchmarks",
    url_2: "https://example.com/resources/benchmarks",
    piece_3: "Change Management Best Practices",
    url_3: "https://example.com/resources/change-mgmt"
  },
  manager: {
    piece_1: "Manager Guide: Daily Workflow Optimization",
    url_1: "https://example.com/resources/manager-guide",
    piece_2: "Team Collaboration Templates",
    url_2: "https://example.com/resources/collab-templates",
    piece_3: "Reporting & Analytics Walkthrough",
    url_3: "https://example.com/resources/analytics-guide"
  },
  technical: {
    piece_1: "API Documentation & Integration Guide",
    url_1: "https://example.com/docs/api",
    piece_2: "Architecture Overview & Security Model",
    url_2: "https://example.com/docs/architecture",
    piece_3: "Developer SDK & Sample Projects",
    url_3: "https://example.com/docs/sdk"
  },
  operations: {
    piece_1: "Process Automation Playbook",
    url_1: "https://example.com/resources/automation-playbook",
    piece_2: "Workflow Templates Library",
    url_2: "https://example.com/resources/workflow-templates",
    piece_3: "Efficiency Metrics Dashboard Guide",
    url_3: "https://example.com/resources/efficiency-metrics"
  },
  individual_contributor: {
    piece_1: "Quick Start Guide",
    url_1: "https://example.com/resources/quick-start",
    piece_2: "Tips & Tricks for Power Users",
    url_2: "https://example.com/resources/power-user-tips",
    piece_3: "Community Forum & Resources",
    url_3: "https://example.com/community"
  }
};
const content = matrix[persona] || matrix.individual_contributor;
return {
  rec_piece_1: content.piece_1,
  rec_url_1: content.url_1,
  rec_piece_2: content.piece_2,
  rec_url_2: content.url_2,
  rec_piece_3: content.piece_3,
  rec_url_3: content.url_3
};`,
          outputVariables: [
            { name: "rec_piece_1", type: "string" },
            { name: "rec_url_1", type: "string" },
            { name: "rec_piece_2", type: "string" },
            { name: "rec_url_2", type: "string" },
            { name: "rec_piece_3", type: "string" },
            { name: "rec_url_3", type: "string" },
          ],
        },
      }),

      // 5. AI content curator
      aiNode(
        "5",
        "AI Curate Personalized Content",
        1000,
        "You are a content personalization expert. Given the contacts role, industry, and the three content pieces selected for their persona, write personalized descriptions for each that connect the content to their specific situation. Make each piece feel hand-selected for their unique needs. Respond JSON: {intro_message: string, piece_1_pitch: string, piece_2_pitch: string, piece_3_pitch: string, closing_cta: string}",
        "Contact: {contact_name}, {job_title} at {company_name} ({industry}). Persona: {persona_label}. Content focus: {content_focus}. Interests: {interests}. Content 1: {rec_piece_1}. Content 2: {rec_piece_2}. Content 3: {rec_piece_3}. Personalize each recommendation.",
        [
          { field: "intro_message", as: "intro_message", type: "string" },
          { field: "piece_1_pitch", as: "piece_1_pitch", type: "string" },
          { field: "piece_2_pitch", as: "piece_2_pitch", type: "string" },
          { field: "piece_3_pitch", as: "piece_3_pitch", type: "string" },
          { field: "closing_cta", as: "closing_cta", type: "string" },
        ],
      ),

      // 6. Personalized content email
      emailNode(
        "6",
        "Send Role-Based Content Email",
        1240,
        "{contact_email}",
        "Curated for {persona_label}s: Your personalized resource pack",
        "<p>Hi {contact_name},</p><p>{intro_message}</p><h3>1. {rec_piece_1}</h3><p>{piece_1_pitch}</p><p><a href='{rec_url_1}'>Access now</a></p><h3>2. {rec_piece_2}</h3><p>{piece_2_pitch}</p><p><a href='{rec_url_2}'>Access now</a></p><h3>3. {rec_piece_3}</h3><p>{piece_3_pitch}</p><p><a href='{rec_url_3}'>Access now</a></p><p>{closing_cta}</p>",
      ),

      // 7. Role engagement analytics Sheets
      sheetsNode(
        "7",
        "Log Role Engagement Analytics",
        1480,
        "RoleEngagement",
        '[["{contact_name}","{contact_email}","{company_name}","{job_title}","{role_persona}","{content_focus}","{is_decision_maker}","{rec_piece_1}","{rec_piece_2}","{rec_piece_3}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1546. CONTRACT RENEWAL FOR B2B (9 nodes)
  // ===================================================================

  "Contract Renewal for B2B": {
    summary:
      "Runs daily on schedule, scans for upcoming renewals in HubSpot, aggregates account health data from HubSpot, generates renewal strategy with AI, assigns renewal ownership with runCode, sends a 90-day value recap email, waits 30 days, sends a 60-day renewal discussion email, and logs renewal outcome to Sheets.",
    nodes: [
      trigger("Schedule: Daily renewal scanner", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to identify B2B contracts renewing within 90 days and initiate the multi-touch renewal engagement sequence based on account health and deal value",
        setup:
          "Set HubSpot private app token for deal and account data. Set OpenAI API key for renewal strategy generation. Configure email credentials for renewal communications. Set Google Sheets spreadsheet ID for outcome tracking.",
        inputFields: [],
      }),

      // 2. Renewal scanner HubSpot
      hubspotGetNode(
        "2",
        "Scan Upcoming Renewals in HubSpot",
        280,
        "crm/v3/objects/deals/search?properties=dealname,amount,closedate,hs_next_step,pipeline,dealstage,hubspot_owner_id,contract_end_date",
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.contract_end_date",
            customName: "contract_end_date",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
          { name: "total", customName: "total_renewals_found", type: "number" },
        ],
      ),

      // 3. Account health aggregator HubSpot
      hubspotGetNode(
        "3",
        "Aggregate Account Health from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}/associations/companies?properties=name,annualrevenue,num_associated_contacts,nps_score,support_tickets_open,last_login_date,feature_usage_score",
        [
          {
            name: "results[0].properties.name",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "annual_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.nps_score",
            customName: "nps_score",
            type: "string",
          },
          {
            name: "results[0].properties.support_tickets_open",
            customName: "open_tickets",
            type: "string",
          },
          {
            name: "results[0].properties.last_login_date",
            customName: "last_login",
            type: "string",
          },
          {
            name: "results[0].properties.feature_usage_score",
            customName: "usage_score",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_contacts",
            customName: "total_contacts",
            type: "string",
          },
        ],
      ),

      // 4. AI renewal strategy generator
      aiNode(
        "4",
        "AI Generate Renewal Strategy",
        760,
        "You are a B2B renewal strategist. Based on the deal value, account health metrics (NPS, usage, support tickets, login recency), and contract timeline, generate a renewal strategy. Determine if this is a straightforward renewal, needs an expansion conversation, or is at risk. Recommend the best approach, pricing strategy, and value messaging. Respond JSON: {renewal_type: string, risk_level: string, expansion_opportunity: string, recommended_approach: string, pricing_strategy: string, value_recap_points: string, key_wins_to_highlight: string, upsell_recommendation: string, renewal_confidence_pct: string}",
        "Deal: {deal_name} worth ${deal_amount}. Company: {company_name}. Contract end: {contract_end_date}. NPS: {nps_score}. Usage score: {usage_score}. Open tickets: {open_tickets}. Last login: {last_login}. Total contacts: {total_contacts}. Annual revenue: ${annual_revenue}. Generate the optimal renewal strategy.",
        [
          { field: "renewal_type", as: "renewal_type", type: "string" },
          { field: "risk_level", as: "renewal_risk_level", type: "string" },
          {
            field: "expansion_opportunity",
            as: "expansion_opportunity",
            type: "string",
          },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
          { field: "pricing_strategy", as: "pricing_strategy", type: "string" },
          {
            field: "value_recap_points",
            as: "value_recap_points",
            type: "string",
          },
          { field: "key_wins_to_highlight", as: "key_wins", type: "string" },
          {
            field: "upsell_recommendation",
            as: "upsell_recommendation",
            type: "string",
          },
          {
            field: "renewal_confidence_pct",
            as: "renewal_confidence",
            type: "string",
          },
        ],
      ),

      // 5. Renewal owner assigner runCode
      n("5", "runCode", "Assign Renewal Owner and Timeline", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign renewal ownership and compute key dates
const amount = parseFloat(vars.deal_amount || "0");
const risk = (vars.renewal_risk_level || "low").toLowerCase();
const contractEnd = new Date(vars.contract_end_date || Date.now());
const now = new Date();
const daysToRenewal = Math.floor((contractEnd - now) / 86400000);
let renewalOwner = "account_manager";
if (amount >= 100000 || risk === "high") renewalOwner = "senior_account_exec";
else if (amount >= 50000) renewalOwner = "account_executive";
const day60 = new Date(contractEnd - 60 * 86400000).toISOString().split("T")[0];
const day30 = new Date(contractEnd - 30 * 86400000).toISOString().split("T")[0];
const day14 = new Date(contractEnd - 14 * 86400000).toISOString().split("T")[0];
return {
  renewal_owner: renewalOwner,
  days_to_renewal: daysToRenewal,
  day_60_date: day60,
  day_30_date: day30,
  day_14_date: day14,
  renewal_priority: risk === "high" ? "critical" : amount >= 50000 ? "high" : "standard",
  contact_email: "renewals@company.com"
};`,
          outputVariables: [
            { name: "renewal_owner", type: "string" },
            { name: "days_to_renewal", type: "number" },
            { name: "day_60_date", type: "string" },
            { name: "day_30_date", type: "string" },
            { name: "day_14_date", type: "string" },
            { name: "renewal_priority", type: "string" },
            { name: "contact_email", type: "string" },
          ],
        },
      }),

      // 6. 90-day value recap email
      emailNode(
        "6",
        "Send 90-Day Value Recap Email",
        1240,
        "{contact_email}",
        "Your year with us: a value recap for {company_name}",
        "<h2>Your Partnership Recap</h2><p>Hi team at {company_name},</p><p>As your contract renewal approaches on {contract_end_date}, we wanted to take a moment to highlight the value we have delivered together:</p><h3>Key Wins</h3><p>{key_wins}</p><h3>Value Delivered</h3><p>{value_recap_points}</p><h3>Usage Highlights</h3><ul><li><strong>Usage Score:</strong> {usage_score}</li><li><strong>Active Contacts:</strong> {total_contacts}</li><li><strong>NPS Score:</strong> {nps_score}</li></ul><p>We are excited to continue this partnership. Your renewal owner will be reaching out soon to discuss next steps and any new capabilities that could benefit your team.</p><p>Best,<br/>Customer Success Team</p>",
      ),

      // 7. Delay 30 days
      n("7", "delayAction", "Wait 30 Days (60 Days Before Renewal)", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 30,
          delayUnit: "days",
          description:
            "Wait 30 days after the value recap (now 60 days before renewal) before sending the renewal discussion email",
        },
      }),

      // 8. 60-day renewal discussion email
      emailNode(
        "8",
        "Send 60-Day Renewal Discussion Email",
        1720,
        "{contact_email}",
        "Renewal discussion: {deal_name} — let us plan your next chapter",
        "<p>Hi team at {company_name},</p><p>Your contract renewal is now about 60 days away ({contract_end_date}). We want to make sure the process is smooth and that your next term sets you up for even greater success.</p><h3>Recommended for Your Renewal</h3><p><strong>Approach:</strong> {recommended_approach}</p><p><strong>Expansion Opportunity:</strong> {expansion_opportunity}</p><p><strong>Recommendation:</strong> {upsell_recommendation}</p><p>Let us schedule a 30-minute renewal discussion to finalize terms and explore any new capabilities that could help your team.</p><p><a href='https://calendly.com/renewals/discussion?company={company_name}'>Book your renewal discussion</a></p><p>Looking forward to continuing our partnership.</p><p>Best,<br/>Renewals Team</p>",
      ),

      // 9. Renewal outcome logger Sheets
      sheetsNode(
        "9",
        "Log Renewal Pipeline to Sheets",
        1960,
        "RenewalPipeline",
        '[["{deal_name}","{company_name}","{deal_amount}","{contract_end_date}","{days_to_renewal}","{renewal_risk_level}","{renewal_confidence}","{renewal_type}","{expansion_opportunity}","{renewal_owner}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1547. CONTRACT SENT SIGNATURE CHASE (8 nodes)
  // ===================================================================

  "Contract Sent Signature Chase": {
    summary:
      "Fires when a contract is sent for signature via webhook, extracts contract data with AI, sends an initial delivery confirmation email, waits 3 days, checks if the contract was viewed via condition, sends a gentle reminder email, waits another 3 days, then applies urgency via a condition to determine if further action is needed.",
    nodes: [
      trigger("Webhook: Contract sent for signature", "webhook_app", {
        source: "docusign",
        event: "envelope.sent",
        howItStarts:
          "Fires when a contract or agreement is sent for electronic signature via DocuSign, PandaDoc, or similar e-signature platform — initiating the signature chase sequence",
        setup:
          "Configure e-signature platform webhook for envelope/document sent events. Set OpenAI API key for contract data extraction. Configure email credentials for chase sequence.",
        inputFields: [],
      }),

      // 2. AI extract contract data
      aiNode(
        "2",
        "AI Extract Contract Details",
        280,
        "You are an e-signature workflow parser. Extract the contract title, recipient name, recipient email, sender name, sender email, contract value, expiration date for signing, number of signers required, document ID, and the signing URL from the raw webhook payload. Respond JSON: {contract_title: string, recipient_name: string, recipient_email: string, sender_name: string, sender_email: string, contract_value: string, signing_deadline: string, signers_required: string, document_id: string, signing_url: string, envelope_status: string, sent_timestamp: string}",
        "Raw e-signature webhook payload: {webhook_payload}. Extract all contract sending data regardless of field naming.",
        [
          { field: "contract_title", as: "contract_title", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "contract_value", as: "contract_value", type: "string" },
          { field: "signing_deadline", as: "signing_deadline", type: "string" },
          { field: "signers_required", as: "signers_required", type: "string" },
          { field: "document_id", as: "document_id", type: "string" },
          { field: "signing_url", as: "signing_url", type: "string" },
          { field: "envelope_status", as: "envelope_status", type: "string" },
          { field: "sent_timestamp", as: "sent_timestamp", type: "string" },
        ],
      ),

      // 3. Initial delivery confirmation email
      emailNode(
        "3",
        "Send Contract Delivery Confirmation",
        520,
        "{recipient_email}",
        "Your contract is ready for review: {contract_title}",
        "<p>Hi {recipient_name},</p><p><strong>{sender_name}</strong> has sent you a contract for your review and signature:</p><h3>{contract_title}</h3><p><strong>Contract Value:</strong> ${contract_value}</p><p><strong>Signing Deadline:</strong> {signing_deadline}</p><p><strong>Signers Required:</strong> {signers_required}</p><p><a href='{signing_url}' style='display:inline-block;padding:12px 24px;background:#0066cc;color:#fff;text-decoration:none;border-radius:4px;'>Review & Sign Now</a></p><p>If you have any questions about the terms, please reach out to {sender_name} at {sender_email}.</p><p>Best,<br/>The Team</p>",
      ),

      // 4. Delay 3 days
      n("4", "delayAction", "Wait 3 Days for Initial Review", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after sending the contract to allow the recipient time to review before following up",
        },
      }),

      // 5. View status monitor condition
      n("5", "condition", "Is Envelope Still Pending (Not Signed)?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "envelope_status", operator: "eq", value: "sent" },
          ],
        },
      }),

      // 6. Gentle reminder email
      emailNode(
        "6",
        "Send Gentle Reminder Email",
        1240,
        "{recipient_email}",
        "Quick reminder: {contract_title} is awaiting your signature",
        "<p>Hi {recipient_name},</p><p>Just a friendly reminder that the contract <strong>{contract_title}</strong> is waiting for your signature. It was sent 3 days ago and we want to make sure it did not get lost in your inbox.</p><p><strong>Key Details:</strong></p><ul><li>Contract Value: ${contract_value}</li><li>Signing Deadline: {signing_deadline}</li><li>Document: {contract_title}</li></ul><p><a href='{signing_url}' style='display:inline-block;padding:12px 24px;background:#0066cc;color:#fff;text-decoration:none;border-radius:4px;'>Sign Now</a></p><p>If you have questions or need changes to the terms, just reply to this email and we will get back to you quickly.</p><p>Best,<br/>{sender_name}</p>",
      ),

      // 7. Delay 3 more days
      n("7", "delayAction", "Wait 3 More Days Before Urgency Nudge", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait another 3 days after the gentle reminder before escalating with an urgency nudge",
        },
      }),

      // 8. Urgency nudge condition
      n("8", "condition", "Is Contract Still Unsigned After 6 Days?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "envelope_status", operator: "eq", value: "sent" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
